import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  FormControlLabel,
  Switch,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AssignmentInd as AssignmentIndIcon,
  CalculateOutlined as CalculateOutlinedIcon,
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { handleEnterAsTabNavigation } from '../../utils/enterAsTab';
import api from '../../services/api';
import facturaCompraService, {
  FacturaCompraFormularioDto,
  CreateItemCompraDto,
  FacturaCompraCreatePayload,
  FacturaCompraPrecargaDesdePdfResult,
} from '../../services/facturaCompraService';
import remitoCompraService, {
  mapRemitoCompraItemToDto,
  RemitoCompra,
  RemitoCompraCreatePayload,
} from '../../services/remitoCompraService';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import {
  pendientesAccionCellSx,
  pendientesArticuloCellSx,
  pendientesTableSx,
} from '../../components/compras/pendientesTableSx';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import ObservacionesConNotaProveedor from '../../components/ObservacionesConNotaProveedor';
import UbicacionSearch from '../../components/UbicacionSearch';
import { useAutoUbicacionPredeterminada } from '../../hooks/useAutoUbicacionPredeterminada';
import ImputableSearch from '../../components/ImputableSearch';
import productoService from '../../services/productoService';
import {
  cargarProductoParaDocumento,
  clearRechazoDiscontinuado,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  evaluarUsoProductoDocumento,
  mensajeArticuloDiscontinuadoBloqueado,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../utils/bloqueaArtDiscontinuados';
import impuestoService from '../../services/impuestoService';
import variablesSistemaService from '../../services/variablesSistemaService';
import comprasSeguimientoService, {
  CompraPendienteMaster,
  cantidadSugeridaDesdePendiente,
  costoBrutoDesdePendienteMaster,
  esProductoInventariado,
  filtrarCompraPendientesPorBusqueda,
  filtrarCompraPendientesPorFechaEmision,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
  porcDescDesdePendienteMaster,
} from '../../services/comprasSeguimientoService';
import { Imputable } from '../../services/imputableService';
import { IUbicacion } from '../../interfaces/Ubicacion';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import { formaDePagoDesdeCondVtaProveedor } from '../../utils/formaPagoCompra';
import { formatCurrency, formatCuit, normalizeCuitForStorage, toLocalDateStringYMD } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  aplicarPrecargaEncabezado,
  type ProveedorInicialDesdePrecarga,
} from '../../utils/compraFacturaPrecarga';
import { readCompaniaDefaultCompras } from '../../utils/companiaDefaultCompras';
import { esRemitoCompraAnulado } from '../../utils/remitoPuedeAnular';
import PrecargaFacturaCompraPdfDialog from '../../components/compras/PrecargaFacturaCompraPdfDialog';
import NuevoProveedorDesdePrecargaDialog from '../../components/compras/NuevoProveedorDesdePrecargaDialog';
import FacturaCompraArticulosSearchBar, {
  type CompraArticuloSuggestion,
} from '../../components/compras/FacturaCompraArticulosSearchBar';
import ItemNotasTextField from '../../components/ItemNotasTextField';
import tesoreriaAplicacionesService from '../../services/tesoreriaAplicacionesService';

const NUEVO_PROVEEDOR_VACIO: ProveedorInicialDesdePrecarga = {
  tipoIva: 'I',
  tipoProveedor: 'C',
};

interface Compania {
  idCompania: string;
  nombre: string;
  condIva?: string;
}

const FORMAS_DE_PAGO = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'DEVOLUCION',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA'
];

/** Límite items.ite_notas */
const ITEM_NOTAS_MAX = 500;

function normalizeItemNotas(s?: string | null): string | undefined {
  const t = (s ?? '').trim();
  return t ? t.slice(0, ITEM_NOTAS_MAX) : undefined;
}

/** Formato Letra-Suc-Nro: A-00008-00000012 (usado al facturar desde remito) */
const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number | string): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(5, '0');

  const nro =
    typeof nroComprobante === 'number'
      ? nroComprobante
      : parseInt(String(nroComprobante ?? ''), 10);
  const safeNro = Number.isNaN(nro) ? 0 : nro;
  return `${L}-${suc}-${String(safeNro).padStart(8, '0')}`;
};

const DEBOUNCE_COMPROBANTE_MS = 400;

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; name?: string };
  return e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError';
}

const MSG_COMPROBANTE_DUPLICADO =
  'Ya existe un comprobante con el mismo CUIT, letra, sucursal y número';

const defaultFormulario: FacturaCompraFormularioDto = {
  idCompania: '',
  letra: '',
  nroSuc: 0,
  nroComprobante: 0,
  tipoProveedor: 'C',
  tipoComprobante: '',
  descripcion: '',
  fechaEmision: toLocalDateStringYMD(),
  fechaVencim: toLocalDateStringYMD(),
  aplicacionIva: toLocalDateStringYMD(),
  nroProveedor: 0,
  formaDePago: 'CONTADO',
  nroUbicacionDestino: undefined,
  porcDesc: 0,
  porcDesc2: 0,
  porcDesc3: 0
};

interface ImpuestoAdicionalRow {
  codigoImpuesto: string;
  ctaCtable: number;
  descripcion: string;
  montoNeto: number;
  montoImpuesto: number;
  valor?: number;
  basadoen?: number;
  montoGravMin?: number;
  montoGravMax?: number;
  montoImpuestoMin?: number;
  montoImpuestoMax?: number;
  ctaCtableLabel?: string;
}

type ItemProductoCompraDto = CreateItemCompraDto;

const SUBTOTAL_RESET_KEYS = [
  'cantidad',
  'costoBrutoUnitario',
  'porcDesc1',
  'porcDesc2',
  'porcDesc3',
  'porcDesc4',
  'porcDesc5',
] as const;

/** Grilla compacta (artículos / conceptos): tipografía y padding densos para que entren más columnas. */
const itemsGridTableSx = {
  mb: 2,
  maxWidth: '100%',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    py: 0.5,
    px: 0.75,
    fontSize: '0.78rem',
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.72rem',
    whiteSpace: 'nowrap',
    lineHeight: 1.25,
  },
  '& .MuiInputBase-root': {
    fontSize: '0.78rem',
  },
  '& .MuiInputBase-input': {
    py: '4px',
    px: '6px',
  },
  '& .MuiIconButton-root': {
    p: 0.35,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
  },
} as const;

const defaultItem: CreateItemCompraDto = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  costoBrutoUnitario: 0,
  porcDesc1: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  porcDesc4: 0,
  porcDesc5: 0,
  notas: '',
  tipoProveedor: 'C'
};

const defaultItemConcepto: CreateItemCompraDto = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  costoBrutoUnitario: 0,
  porcDesc1: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  porcDesc4: 0,
  porcDesc5: 0,
  notas: '',
  tipoProveedor: 'G'
};

const isPendienteEnFactura = (
  pendiente: CompraPendienteMaster,
  facturaItems: ItemProductoCompraDto[],
): boolean =>
  facturaItems.some(
    (it) =>
      it.nroFormularioMaster === pendiente.nroFormularioMaster &&
      it.nroItemMaster === pendiente.nroItemMaster,
  );

const buildRemitoUpdatePayload = (remito: RemitoCompra, nroFormularioFactura: number): RemitoCompraCreatePayload => {
  const remitoItems = remito.detalles ?? [];
  return {
    formulario: {
      descripcion: remito.descripcion ?? '',
      nroSuc: remito.nroSuc,
      letra: remito.letra ?? 'A',
      nroComprobante: remito.nroComprobante ?? 1,
      fechaEmision: remito.fechaEmision,
      nroProveedor: remito.nroCliente ?? 0,
      nroUbicacionDestino: remito.nroUbicacionDestino,
      nroFormularioFactura
    },
    items: remitoItems
      .map((d) => {
        const cantidad = Number(d.cantidad) || 0;
        const precioUnitario =
          Number(d.precioUnitario) > 0
            ? Number(d.precioUnitario)
            : cantidad > 0
              ? Math.round(((Number(d.subtotal) || 0) / cantidad) * 100) / 100
              : 0;
        return mapRemitoCompraItemToDto({
          codigoArticulo: (d.codigoArticulo || '').trim(),
          descripcion: d.descripcionArticulo || '',
          cantidad,
          precioUnitario,
          porcDesc: 0,
          notas: normalizeItemNotas(d.notas ?? null) ?? '',
          nroFormularioMaster: d.nroFormularioMaster,
          nroItemMaster: d.nroItemMaster,
        });
      })
      .filter((item) => item.codigoArticulo && item.cantidad > 0 && item.precioUnitario >= 0),
  };
};

const DecimalTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  sx?: any;
  inputProps?: any;
}> = ({ value, onChange, placeholder, label, fullWidth, sx, inputProps }) => {
  const [localValue, setLocalValue] = useState<string>(String(value ?? 0));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setLocalValue(String(value ?? 0));
  }, [value, focused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = String(e.target.value ?? '');
    // Permitimos solo dígitos y separadores decimales
    raw = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    // Evitar más de un '.'
    const parts = raw.split('.');
    if (parts.length > 2) raw = `${parts[0]}.${parts.slice(1).join('')}`;

    setLocalValue(raw);

    // No actualizamos el state numérico si el valor está incompleto
    if (raw === '' || raw === '.' || raw.endsWith('.')) return;
    const n = Number(raw);
    if (!Number.isNaN(n) && Number.isFinite(n)) onChange(n);
  };

  const handleBlur = () => {
    setFocused(false);
    const currentV = Number(value ?? 0);
    const raw = String(localValue ?? '').trim();
    // Si el usuario dejó un '.' final (ej: "25.") lo interpretamos como "25"
    const normalized = raw.endsWith('.') ? raw.slice(0, -1) : raw;
    if (normalized === '' || normalized === '.') {
      setLocalValue('0');
      if (currentV !== 0) onChange(0);
      return;
    }
    const n = Number(normalized);
    const finalV = Number.isNaN(n) ? 0 : n;
    setLocalValue(String(finalV));
    if (finalV !== currentV) onChange(finalV);
  };

  return (
    <TextField
      size="small"
      type="text"
      label={label}
      fullWidth={fullWidth}
      value={localValue}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      sx={sx}
      inputProps={{
        inputMode: 'decimal',
        ...inputProps
      }}
    />
  );
};

const formatPesosInput = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const parsePesosInput = (raw: string): number => {
  const cleaned = (raw ?? '').replace(/\s/g, '').replace(/\$/g, '');
  if (!cleaned) return 0;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  let normalized = cleaned;

  if (hasComma && hasDot) {
    // Formato esperado es-AR: "." miles y "," decimales.
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  }

  // Eliminar cualquier separador residual no numérico (excepto el punto decimal).
  normalized = normalized.replace(/[^0-9.]/g, '');
  const parts = normalized.split('.');
  if (parts.length > 2) {
    normalized = `${parts[0]}.${parts.slice(1).join('')}`;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const CurrencyTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  label?: string;
  fullWidth?: boolean;
  sx?: any;
}> = ({ value, onChange, label, fullWidth, sx }) => {
  const [focused, setFocused] = useState(false);
  const [localValue, setLocalValue] = useState<string>(formatPesosInput(value ?? 0));

  useEffect(() => {
    if (!focused) {
      setLocalValue(formatPesosInput(value ?? 0));
    }
  }, [value, focused]);

  return (
    <TextField
      size="small"
      type="text"
      label={label}
      fullWidth={fullWidth}
      value={localValue}
      onFocus={() => {
        setFocused(true);
        setLocalValue(formatPesosInput(value ?? 0));
      }}
      onChange={(e) => {
        const raw = String(e.target.value ?? '');
        // Permitimos dígitos, coma y punto para facilitar pegado/edición.
        const sanitized = raw.replace(/[^0-9.,]/g, '');
        setLocalValue(sanitized);
        const parsed = parsePesosInput(sanitized);
        onChange(parsed);
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parsePesosInput(localValue);
        onChange(parsed);
        setLocalValue(formatPesosInput(parsed));
      }}
      sx={sx}
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>
      }}
    />
  );
};

const FacturaCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [formulario, setFormulario] = useState<FacturaCompraFormularioDto>({ ...defaultFormulario });
  const setNroSucFormulario = useCallback((nroSuc: number) => {
    setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
  }, []);
  const [itemsProductos, setItemsProductos] = useState<ItemProductoCompraDto[]>([]);
  const [itemsConceptos, setItemsConceptos] = useState<CreateItemCompraDto[]>([]);
  /** Si no es null, reemplaza el IVA total del resumen y se envía como `ivaManual` al API. */
  const [ivaManualUsuario, setIvaManualUsuario] = useState<number | null>(null);
  const [esNotaDebito, setEsNotaDebito] = useState(false);
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [proveedorNombre, setProveedorNombre] = useState('');
  const [proveedorCuit, setProveedorCuit] = useState('');
  const [proveedorTipoIva, setProveedorTipoIva] = useState('');
  const [nuevoProveedorOpen, setNuevoProveedorOpen] = useState(false);
  /** Datos fiscales del PDF cuando el proveedor no estaba en el maestro; se aplican al elegir uno (p. ej. OCASIONAL). */
  const fiscalOverridePrecargaRef = useRef<{
    nombre: string;
    cuit: string;
    tipoIva: string;
  } | null>(null);
  const [ubicacionNombre, setUbicacionNombre] = useState('');
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [precargaDialogOpen, setPrecargaDialogOpen] = useState(false);
  const [precargaLoading, setPrecargaLoading] = useState(false);
  const [precargaResult, setPrecargaResult] = useState<FacturaCompraPrecargaDesdePdfResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmAplicarOpen, setConfirmAplicarOpen] = useState(false);
  const [postCreateNav, setPostCreateNav] = useState<{
    nroFormulario: number;
    pedirEtiquetas: boolean;
  } | null>(null);
  const [comprobanteDuplicado, setComprobanteDuplicado] = useState(false);
  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productos, setProductos] = useState<any[]>([]);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productosPage, setProductosPage] = useState(0);
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);
  const [openPendientes, setOpenPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<CompraPendienteMaster[]>([]);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [agregandoTodos, setAgregandoTodos] = useState(false);
  const [pendientesBusqueda, setPendientesBusqueda] = useState('');
  const [pendientesFechaDesde, setPendientesFechaDesde] = useState('');
  const [pendientesFechaHasta, setPendientesFechaHasta] = useState('');
  const [soloInventariados, setSoloInventariados] = useState(true);
  const [productCache, setProductCache] = useState<Record<string, {
    calculaIva?: string;
    ivaRI?: number;
    ivaCF?: number;
    ivaMT?: number;
    ivaRN?: number;
    nroTasaIva?: number;
  }>>({});
  const [impuestosAdicionales, setImpuestosAdicionales] = useState<ImpuestoAdicionalRow[]>([]);
  const [openImpuestoSearch, setOpenImpuestoSearch] = useState(false);
  const [impuestoSearchTerm, setImpuestoSearchTerm] = useState('');
  const [impuestosSearchResults, setImpuestosSearchResults] = useState<any[]>([]);
  const [impuestosSearchPage, setImpuestosSearchPage] = useState(0);
  const [impuestosSearchTotal, setImpuestosSearchTotal] = useState(0);
  const [openImputableSearchImpuesto, setOpenImputableSearchImpuesto] = useState(false);
  const [imputableSearchImpuestoIndex, setImputableSearchImpuestoIndex] = useState<number | null>(null);
  const [expandOtrosImpuestos, setExpandOtrosImpuestos] = useState(false);
  const [openImputableSearchConcepto, setOpenImputableSearchConcepto] = useState(false);
  const [imputableSearchConceptoIndex, setImputableSearchConceptoIndex] = useState<number | null>(null);
  const [impuestoTasas, setImpuestoTasas] = useState<{ impuestotasa1?: number; impuestotasa2?: number; impuestotasa3?: number; impuestotasa4?: number }>({});
  const [accordionProductosExpanded, setAccordionProductosExpanded] = useState(true);
  const [accordionConceptosExpanded, setAccordionConceptosExpanded] = useState(false);
  const [accordionTotalesExpanded, setAccordionTotalesExpanded] = useState(true);
  const [showDatosEmision, setShowDatosEmision] = useState(true);
  const [showDatosProveedor, setShowDatosProveedor] = useState(false);
  const [showDatosComerciales, setShowDatosComerciales] = useState(false);
  const remitoIdParam = searchParams.get('remitoId');
  const [remitoId, setRemitoId] = useState<number | null>(remitoIdParam ? parseInt(remitoIdParam, 10) : null);
  const [remitoOrigen, setRemitoOrigen] = useState<RemitoCompra | null>(null);
  const compraPendienteFromNav = (location.state as { compraPendiente?: CompraPendienteMaster } | null)?.compraPendiente;
  const compraPendienteNavAppliedRef = useRef(false);
  const agregarPendienteProductoRef = useRef<(p: CompraPendienteMaster) => Promise<void>>(async () => {});
  const agregandoTodosRef = useRef(false);
  const mastersEliminadosRef = useRef<Set<string>>(new Set());
  const itemsProductosRef = useRef(itemsProductos);
  itemsProductosRef.current = itemsProductos;

  const handleFormularioChange = (updates: Partial<FacturaCompraFormularioDto>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  useEffect(() => {
    const letra = (formulario.letra ?? '').trim() || 'A';
    const nro = formulario.nroComprobante ?? 0;
    const cuitNorm = normalizeCuitForStorage(proveedorCuit);
    if (nro <= 0 || !cuitNorm) {
      setComprobanteDuplicado(false);
      return;
    }

    const controller = new AbortController();
    setComprobanteDuplicado(false);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const dup = await facturaCompraService.buscarDuplicadoPorComprobante({
            letra,
            nroSuc: formulario.nroSuc ?? 0,
            nroComprobante: nro,
            cuit: proveedorCuit,
            esNotaDebito,
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          setComprobanteDuplicado(!!dup);
        } catch (error: unknown) {
          if (isAbortError(error) || controller.signal.aborted) return;
          setComprobanteDuplicado(false);
        }
      })();
    }, DEBOUNCE_COMPROBANTE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    formulario.letra,
    formulario.nroSuc,
    formulario.nroComprobante,
    proveedorCuit,
    esNotaDebito,
  ]);

  const pendientesDisponibles = useMemo(
    () => pendientes.filter((p) => !isPendienteEnFactura(p, itemsProductos)),
    [pendientes, itemsProductos],
  );

  const pendientesFiltrados = useMemo(() => {
    let rows = pendientesDisponibles;
    if (soloInventariados) {
      rows = rows.filter((p) => esProductoInventariado(p.invetariado));
    }
    rows = filtrarCompraPendientesPorFechaEmision(rows, pendientesFechaDesde, pendientesFechaHasta);
    return filtrarCompraPendientesPorBusqueda(rows, pendientesBusqueda);
  }, [pendientesDisponibles, soloInventariados, pendientesBusqueda, pendientesFechaDesde, pendientesFechaHasta]);

  const loadCompanias = useCallback(async () => {
    try {
      const res = await api.get('/api/Compañias');
      const data = res.data;
      const list: Compania[] = Array.isArray(data) ? data : Array.isArray((data as any)?.items) ? (data as any).items : [];
      setCompanias(
        list.map((c: any) => ({
          idCompania: String(c.idCompania || '').trim(),
          nombre: String(c.nombre || '').trim(),
          condIva: c.condIva != null ? String(c.condIva).trim() : undefined
        }))
      );
    } catch {
      setCompanias([]);
    }
  }, []);

  useEffect(() => {
    loadCompanias();
  }, [loadCompanias]);

  useEffect(() => {
    variablesSistemaService.getImpuestoTasas().then(setImpuestoTasas).catch(() => ({}));
  }, []);

  useEffect(() => {
    const parsed = remitoIdParam ? parseInt(remitoIdParam, 10) : null;
    setRemitoId(parsed && !Number.isNaN(parsed) ? parsed : null);
  }, [remitoIdParam]);

  const companiaDefaultAplicadaRef = useRef(false);
  useEffect(() => {
    if (companiaDefaultAplicadaRef.current || companias.length === 0) return;
    companiaDefaultAplicadaRef.current = true;
    const idDefault = readCompaniaDefaultCompras();
    if (!idDefault || !companias.some((c) => c.idCompania === idDefault)) return;
    setFormulario((f) => (f.idCompania ? f : { ...f, idCompania: idDefault }));
  }, [companias]);

  useEffect(() => {
    const loadRemito = async () => {
      if (!remitoId || Number.isNaN(remitoId)) return;
      try {
        const remito = await remitoCompraService.getRemitoCompra(remitoId);
        setRemitoOrigen(remito);

        if (esRemitoCompraAnulado(remito)) {
          enqueueSnackbar('No se puede facturar un remito anulado', { variant: 'warning' });
          navigate('/compras/remitos');
          return;
        }

        const remitoItems = remito.detalles ?? [];
        const nroProveedor = remito.nroCliente;
        let pendientesPorMaster = new Map<string, CompraPendienteMaster>();
        if (nroProveedor && nroProveedor > 0) {
          try {
            const pendientes = await comprasSeguimientoService.getPendientesMaster(nroProveedor, false);
            pendientesPorMaster = new Map(
              (pendientes || []).map((p) => [`${p.nroFormularioMaster}-${p.nroItemMaster}`, p])
            );
          } catch {
            /* se usa cantidad del remito si no hay seguimiento */
          }
        }

        const mappedItems: CreateItemCompraDto[] = remitoItems.flatMap((d) => {
          const masterForm =
            d.nroFormularioMaster && d.nroFormularioMaster > 0 ? d.nroFormularioMaster : remitoId;
          const masterItem = d.nroItemMaster && d.nroItemMaster > 0 ? d.nroItemMaster : d.nroItem;
          const pend = pendientesPorMaster.get(`${masterForm}-${masterItem}`);
          const pendienteFacturar = pend ? Number(pend.pendienteFacturar) || 0 : Number(d.cantidad) || 0;
          if (pendienteFacturar <= 0) return [];

          const cantidadRemito = Number(d.cantidad) || 0;
          const cantidadSugerida = pend ? cantidadSugeridaDesdePendiente(pend) : cantidadRemito > 0 ? cantidadRemito : 1;
          const cantidad =
            pend && cantidadRemito > 0 ? Math.min(cantidadSugerida, cantidadRemito) : cantidadSugerida;

          let costoBrutoUnitario = pend ? costoBrutoDesdePendienteMaster(pend) : 0;
          const { porcDesc1, porcDesc2, porcDesc3, porcDesc4, porcDesc5 } = pend
            ? porcDescDesdePendienteMaster(pend)
            : { porcDesc1: 0, porcDesc2: 0, porcDesc3: 0, porcDesc4: 0, porcDesc5: 0 };
          if (costoBrutoUnitario <= 0) {
            costoBrutoUnitario =
              Number(d.precioUnitario) > 0
                ? Number(d.precioUnitario)
                : cantidadRemito > 0
                  ? Math.round(((Number(d.subtotal) || 0) / cantidadRemito) * 100) / 100
                  : 0;
          }

          const codigoArticulo = (d.codigoArticulo || '').trim();
          const cantidadFinal = cantidad > 0 ? cantidad : 1;
          if (!codigoArticulo || cantidadFinal <= 0) return [];

          const item: CreateItemCompraDto = {
            ...defaultItem,
            codigoArticulo,
            descripcion: d.descripcionArticulo || pend?.descripcionArticulo || '',
            cantidad: cantidadFinal,
            costoBrutoUnitario,
            porcDesc1,
            porcDesc2,
            porcDesc3,
            porcDesc4,
            porcDesc5,
            nroFormularioMaster: masterForm,
            nroItemMaster: masterItem,
            notas: normalizeItemNotas(d.notas ?? null) ?? '',
          };
          return [item];
        });

        if (mappedItems.length === 0) {
          enqueueSnackbar('El remito no tiene cantidades pendientes de facturar', { variant: 'info' });
          navigate('/compras/remitos');
          return;
        }

        setFormulario((prev) => ({
          ...prev,
          nroProveedor: remito.nroCliente ?? prev.nroProveedor,
          nroUbicacionDestino: remito.nroUbicacionDestino ?? prev.nroUbicacionDestino,
          letra: (() => {
            const L = (remito.letra || prev.letra || 'A').trim().toUpperCase();
            return L === 'B' ? 'A' : L || 'A';
          })(),
          nroSuc: remito.nroSuc || prev.nroSuc || 1,
          nroComprobante: remito.nroComprobante || prev.nroComprobante || 1,
          descripcion: prev.descripcion || remito.descripcion || ''
        }));
        setProveedorNombre(remito.nombre || '');
        setProveedorCuit(remito.cuit || '');
        if (nroProveedor && nroProveedor > 0) {
          try {
            const prov = await proveedorService.getProveedor(nroProveedor);
            setProveedorNombre((prev) => prev || prov.nombre || '');
            setProveedorCuit((prev) => prev || prov.cuit || '');
            setProveedorTipoIva((prov.tipoIva || '').trim().toUpperCase());
          } catch {
            /* nombre/cuit del remito alcanzan si el maestro no responde */
          }
        }
        if (mappedItems.length > 0) {
          setItemsProductos(mappedItems);
          setItemsConceptos([]);
          setAccordionProductosExpanded(true);
          setAccordionConceptosExpanded(false);

          // El IVA del formulario se calcula usando `productCache` (calculaIva/ivaX por letra).
          // Cuando facturamos desde un remito, el usuario aún no interactuó con el buscador de productos,
          // así que precargamos el cache de IVA para cada código de artículo.
          setProductCache({});
          const codigos = Array.from(new Set(mappedItems.map((it) => (it.codigoArticulo || '').trim()).filter(Boolean)));
          if (codigos.length > 0) {
            const cacheEntries = await Promise.all(
              codigos.map(async (codigo) => {
                try {
                  const p = await cargarProductoParaDocumento(codigo, 'COMPRAS');
                  const pr: any = p;
                  return [
                    codigo,
                    {
                      calculaIva: typeof pr.calculaIva === 'string' ? pr.calculaIva : undefined,
                      ivaRI: pr.ivaRI != null ? Number(pr.ivaRI) : undefined,
                      ivaCF: pr.ivaCF != null ? Number(pr.ivaCF) : undefined,
                      ivaMT: pr.ivaMT != null ? Number(pr.ivaMT) : undefined,
                      ivaRN: pr.ivaRN != null ? Number(pr.ivaRN) : undefined,
                      nroTasaIva: pr.nroTasaIva != null ? Number(pr.nroTasaIva) : undefined
                    }
                  ] as const;
                } catch {
                  return null;
                }
              })
            );
            const nextCache: Record<string, { calculaIva?: string; ivaRI?: number; ivaCF?: number; ivaMT?: number; ivaRN?: number; nroTasaIva?: number }> =
              Object.fromEntries(cacheEntries.filter(Boolean).map((e) => e as any));
            setProductCache((prev) => ({ ...prev, ...nextCache }));
          }
        }
      } catch {
        enqueueSnackbar('No se pudo cargar el remito para facturar', { variant: 'error' });
      }
    };

    loadRemito();
  }, [enqueueSnackbar, navigate, remitoId]);

  /** Sugerir letra según TipoIva del proveedor (I/M->A, C/CF->C, X->X; resto->A) */
  const letraFromTipoIva = (tipoIva: string | undefined): string => {
    const t = (tipoIva ?? '').trim().toUpperCase();
    if (t === 'X') return 'X';
    if (t === 'C' || t === 'CF') return 'C';
    if (t === 'I' || t === 'M') return 'A';
    return 'A';
  };

  const handleProveedorClear = () => {
    fiscalOverridePrecargaRef.current = null;
    setShowDatosProveedor(false);
    setFormulario((prev) => ({ ...prev, nroProveedor: 0 }));
    setProveedorNombre('');
    setProveedorCuit('');
    setProveedorTipoIva('');
  };

  const handleProveedorTipoIvaChange = (tipoIva: string | null) => {
    const next = (tipoIva ?? '').trim().toUpperCase();
    setProveedorTipoIva(next);
    let letraSugerida = letraFromTipoIva(next);
    const companiaEmisora = companias.find((c) => c.idCompania === formulario.idCompania);
    const condIvaNorm = (companiaEmisora?.condIva ?? '')
      .toUpperCase()
      .replace(/\./g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (condIvaNorm === 'RESP MONOTRIBUTO' || condIvaNorm.includes('MONOTRIBUTO')) {
      letraSugerida = 'C';
    }
    handleFormularioChange({ letra: letraSugerida });
  };

  const handleProveedorSelect = async (proveedor: Proveedor) => {
    let datosProveedor: Proveedor = proveedor;
    try {
      const full = await proveedorService.getProveedor(proveedor.nroProveedor);
      datosProveedor = { ...proveedor, ...full };
    } catch {
      /* se usa la fila del buscador */
    }

    const overrideFiscal = fiscalOverridePrecargaRef.current;
    fiscalOverridePrecargaRef.current = null;
    if (overrideFiscal) {
      setShowDatosProveedor(true);
    }

    const nombreFinal = overrideFiscal?.nombre?.trim() || datosProveedor.nombre || '';
    const cuitFinal = overrideFiscal?.cuit?.trim() || datosProveedor.cuit || '';
    const tipoIvaFinal =
      overrideFiscal?.tipoIva?.trim() || (datosProveedor.tipoIva || '').trim().toUpperCase();

    let letraSugerida = letraFromTipoIva(tipoIvaFinal || datosProveedor.tipoIva);

    // Si la compañía emisora está configurada como "RESP. MONOTRIBUTO",
    // la letra del comprobante siempre debe ser C.
    const companiaEmisora = companias.find((c) => c.idCompania === formulario.idCompania);
    const condIvaNorm = (companiaEmisora?.condIva ?? '')
      .toUpperCase()
      .replace(/\./g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (condIvaNorm === 'RESP MONOTRIBUTO' || condIvaNorm.includes('MONOTRIBUTO')) {
      letraSugerida = 'C';
    }

    const tipoProveedorSugerido = (datosProveedor.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'G' : 'C';
    const fallbackFp =
      formulario.formaDePago && FORMAS_DE_PAGO.includes(formulario.formaDePago)
        ? formulario.formaDePago
        : 'CONTADO';
    const formaDePagoSugerida = formaDePagoDesdeCondVtaProveedor(FORMAS_DE_PAGO, datosProveedor, fallbackFp);
    setFormulario((f) => ({
      ...f,
      nroProveedor: datosProveedor.nroProveedor,
      letra: letraSugerida,
      tipoProveedor: tipoProveedorSugerido,
      formaDePago: formaDePagoSugerida
    }));
    setProveedorNombre(nombreFinal);
    setProveedorCuit(cuitFinal);
    setProveedorTipoIva(tipoIvaFinal);
    if (tipoProveedorSugerido === 'G') {
      setAccordionProductosExpanded(false);
      setAccordionConceptosExpanded(true);
    } else {
      setAccordionProductosExpanded(true);
      setAccordionConceptosExpanded(false);
    }
  };

  const handlePrecargaPdfPick = () => {
    pdfInputRef.current?.click();
  };

  const handlePrecargaPdfSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setPrecargaDialogOpen(true);
    setPrecargaLoading(true);
    setPrecargaResult(null);

    try {
      const result = await facturaCompraService.precargarDesdePdf(file);
      setPrecargaResult(result);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo analizar el PDF.'), { variant: 'error' });
      setPrecargaDialogOpen(false);
    } finally {
      setPrecargaLoading(false);
    }
  };

  const handleApplyPrecarga = async () => {
    if (!precargaResult) return;

    let proveedorDetalle: Proveedor | null = null;
    if (precargaResult.proveedor.encontrado && precargaResult.proveedor.nroProveedor) {
      try {
        proveedorDetalle = await proveedorService.getProveedor(precargaResult.proveedor.nroProveedor);
      } catch {
        /* datos parciales de la precarga */
      }
    }

    const applied = aplicarPrecargaEncabezado({
      precarga: precargaResult,
      formularioActual: formulario,
      companias,
      formasDePago: FORMAS_DE_PAGO,
      proveedorDetalle,
      impuestoTasas,
    });

    setFormulario(applied.formulario);
    setProveedorNombre(applied.proveedorNombre);
    setProveedorCuit(applied.proveedorCuit || proveedorDetalle?.cuit || '');
    setProveedorTipoIva(applied.proveedorTipoIva || proveedorDetalle?.tipoIva || '');
    setEsNotaDebito(applied.esNotaDebito);

    const sinProveedorMaestro =
      !precargaResult.proveedor.encontrado || !precargaResult.proveedor.nroProveedor;
    if (sinProveedorMaestro && (applied.proveedorNombre.trim() || applied.proveedorCuit.trim())) {
      fiscalOverridePrecargaRef.current = {
        nombre: applied.proveedorNombre.trim(),
        cuit: applied.proveedorCuit.trim(),
        tipoIva: (applied.proveedorTipoIva || '').trim(),
      };
    } else {
      fiscalOverridePrecargaRef.current = null;
    }

    if (applied.itemsProductos.length > 0) {
      setItemsProductos(applied.itemsProductos);
    }
    if (applied.itemsConceptos.length > 0) {
      setItemsConceptos(applied.itemsConceptos);
    }

    const tieneConceptos = applied.itemsConceptos.length > 0;
    const tieneProductos = applied.itemsProductos.length > 0;
    if (applied.expandConceptos || (tieneConceptos && !tieneProductos)) {
      setAccordionProductosExpanded(false);
      setAccordionConceptosExpanded(true);
    } else if (tieneProductos || tieneConceptos) {
      setAccordionProductosExpanded(tieneProductos);
      setAccordionConceptosExpanded(tieneConceptos);
    } else if (applied.expandConceptos) {
      setAccordionProductosExpanded(false);
      setAccordionConceptosExpanded(true);
    } else {
      setAccordionProductosExpanded(true);
      setAccordionConceptosExpanded(false);
    }
    setPrecargaDialogOpen(false);
    const detalleMsg =
      applied.itemsProductos.length + applied.itemsConceptos.length > 0
        ? ` (${applied.itemsProductos.length} productos, ${applied.itemsConceptos.length} conceptos)`
        : '';
    enqueueSnackbar(`Precarga aplicada${detalleMsg}. Revise compañía, ubicación y datos antes de guardar.`, {
      variant: 'success',
      autoHideDuration: 5000,
    });
  };

  const handleProveedorCreadoDesdePrecarga = (proveedor: Proveedor) => {
    setPrecargaResult((prev) =>
      prev
        ? {
            ...prev,
            proveedor: {
              encontrado: true,
              nroProveedor: proveedor.nroProveedor,
              nombre: proveedor.nombre,
              cuit: proveedor.cuit ?? prev.proveedor.cuit,
            },
          }
        : prev,
    );
    enqueueSnackbar(`Proveedor #${proveedor.nroProveedor} creado y vinculado a la precarga`, {
      variant: 'success',
    });
  };

  const handleProveedorCreadoNuevo = (proveedor: Proveedor) => {
    setNuevoProveedorOpen(false);
    void handleProveedorSelect(proveedor);
    enqueueSnackbar(`Proveedor #${proveedor.nroProveedor} creado`, { variant: 'success' });
  };

  const handleUbicacionSelect = (ubicacion: IUbicacion) => {
    setFormulario((f) => ({ ...f, nroUbicacionDestino: ubicacion.nroUbicacion }));
    setUbicacionNombre(ubicacion.nombreUbicacion || `Ubicación ${ubicacion.nroUbicacion}`);
    setOpenUbicacionSearch(false);
  };

  const applyUbicacionPredeterminada = useCallback((nro: number, nombre: string) => {
    setFormulario((f) => ({ ...f, nroUbicacionDestino: nro }));
    setUbicacionNombre(nombre);
  }, []);

  useAutoUbicacionPredeterminada({
    nroUbicacionDestino: formulario.nroUbicacionDestino,
    onApply: applyUbicacionPredeterminada,
  });

  const updateItemProducto = (index: number, updates: Partial<ItemProductoCompraDto>) => {
    setItemsProductos((prev) => {
      const next = [...prev];
      const shouldClearManual =
        !('subtotalManual' in updates) &&
        SUBTOTAL_RESET_KEYS.some((k) => k in updates && updates[k] !== undefined);
      next[index] = {
        ...next[index],
        ...updates,
        ...(shouldClearManual ? { subtotalManual: undefined } : {}),
      };
      return next;
    });
  };

  const addItemProducto = () => {
    setAccordionProductosExpanded(true);
    setItemsProductos((prev) => [...prev, { ...defaultItem }]);
  };

  const handleAgregarProductoDesdeBusqueda = useCallback(
    (item: CompraArticuloSuggestion) => {
      const codigo = String(item.codigoArticulo ?? '').trim();
      if (!codigo) return;
      clearRechazoDiscontinuado('COMPRAS', codigo);
      setAccordionProductosExpanded(true);
      const cantidad = item.cantidad && item.cantidad > 0 ? item.cantidad : 1;
      const costo = Number(item.costo) || 0;
      const um = String(item.um ?? '').trim();
      setItemsProductos((prev) => [
        ...prev,
        {
          ...defaultItem,
          codigoArticulo: codigo,
          descripcion: item.descripcion || '',
          cantidad,
          costoBrutoUnitario: costo,
          um,
        },
      ]);
      const p = item.producto as
        | (NonNullable<CompraArticuloSuggestion['producto']> & { nroTasaIva?: number | null })
        | undefined;
      if (p) {
        setProductCache((prev) => ({
          ...prev,
          [codigo]: {
            calculaIva: typeof p.calculaIva === 'string' ? p.calculaIva : undefined,
            ivaRI: p.ivaRI != null ? Number(p.ivaRI) : undefined,
            ivaCF: p.ivaCF != null ? Number(p.ivaCF) : undefined,
            ivaMT: p.ivaMT != null ? Number(p.ivaMT) : undefined,
            ivaRN: p.ivaRN != null ? Number(p.ivaRN) : undefined,
            nroTasaIva: p.nroTasaIva != null ? Number(p.nroTasaIva) : undefined,
          },
        }));
      }
    },
    [],
  );

  const removeItemProducto = (index: number) => {
    setItemsProductos((prev) => {
      const removed = prev[index];
      const mf = removed?.nroFormularioMaster;
      const mi = removed?.nroItemMaster;
      if (mf && mi) mastersEliminadosRef.current.add(`${mf}-${mi}`);
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateItemConcepto = (index: number, updates: Partial<CreateItemCompraDto>) => {
    setItemsConceptos((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const addItemConcepto = () => {
    setItemsConceptos((prev) => [...prev, { ...defaultItemConcepto }]);
  };

  const removeItemConcepto = (index: number) => {
    setItemsConceptos((prev) => prev.filter((_, i) => i !== index));
  };

  type ProductIvaCache = { calculaIva?: string; ivaRI?: number; ivaCF?: number; ivaMT?: number; ivaRN?: number; nroTasaIva?: number };

  const getIvaPorcForLetra = (letra: string, product: ProductIvaCache | null): number => {
    if (!letra || !product) return 0;
    const L = letra.trim().toUpperCase();
    if (L === 'A') return product.ivaRI ?? 0;
    if (L === 'B') return product.ivaCF ?? 0;
    if (L === 'C') return product.ivaMT ?? 0;
    if (L === 'X') return product.ivaRN ?? 0;
    return 0;
  };

  /** Calcula tasa1-5 según stock.calculaiva, letra (A->IvaRI) y stock.NroTasaIVA.
   * Letras B, C y X no calculan IVA en compras. Factura A + nroTasaIva 4 (exento): tasa4 = monto exento con descuento aplicado. */
  const computeTasasForItem = (
    item: CreateItemCompraDto,
    product: ProductIvaCache | null,
    letra: string,
    factorBonif: number
  ): { tasa1: number; tasa2: number; tasa3: number; tasa4: number; tasa5: number } => {
    const zeros = { tasa1: 0, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: 0 };
    if (letra === 'B' || letra === 'C' || letra === 'X') return zeros;

    const subBrutoItem = subtotalEfectivoItem(item);

    const nroTasa = product?.nroTasaIva ?? 0;
    if (letra === 'A' && (nroTasa === 4 || nroTasa === 5)) {
      const montoExentoConDesc = Math.round(subBrutoItem * factorBonif * 100) / 100;
      return nroTasa === 4 ? { ...zeros, tasa4: montoExentoConDesc } : { ...zeros, tasa5: montoExentoConDesc };
    }

    if (!product || (product.calculaIva ?? '').trim().toUpperCase() !== 'S') return zeros;
    const porcentaje = getIvaPorcForLetra(letra, product);
    if (porcentaje <= 0) return zeros;
    const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
    const montoIVA = Math.round(subNetoItem * (porcentaje / 100) * 100) / 100;
    const nro = Math.min(5, Math.max(1, product.nroTasaIva ?? 1));
    switch (nro) {
      case 1: return { ...zeros, tasa1: montoIVA };
      case 2: return { ...zeros, tasa2: montoIVA };
      case 3: return { ...zeros, tasa3: montoIVA };
      case 4: return { ...zeros, tasa4: montoIVA };
      case 5: return { ...zeros, tasa5: montoIVA };
      default: return { ...zeros, tasa1: montoIVA };
    }
  };

  const subtotalItem = (item: CreateItemCompraDto) => {
    const qty = Number(item.cantidad) || 0;
    const costo = Number(item.costoBrutoUnitario) || 0;
    const d1 = 1 - (Number(item.porcDesc1) || 0) / 100;
    const d2 = 1 - (Number(item.porcDesc2) || 0) / 100;
    const d3 = 1 - (Number(item.porcDesc3) || 0) / 100;
    const d4 = 1 - (Number(item.porcDesc4) || 0) / 100;
    const d5 = 1 - (Number(item.porcDesc5) || 0) / 100;
    return Math.round(qty * costo * d1 * d2 * d3 * d4 * d5 * 100) / 100;
  };

  const subtotalEfectivoItem = (item: CreateItemCompraDto) => {
    if (item.subtotalManual !== undefined) {
      return Math.round(item.subtotalManual * 100) / 100;
    }
    return subtotalItem(item);
  };

  const letra = formulario.letra?.trim().toUpperCase() || '';
  const esLetraC = letra === 'C';
  const columnasConceptos = esLetraC ? 6 : 10;

  /** Producto exento (tasa4 y tasa5 = nroTasaIva 4 y 5) en Factura A: no gravado, va a IVA Exento/Monto Exento */
  const esExento = (prod: ProductIvaCache | null) => {
    const nro = prod?.nroTasaIva ?? 0;
    return letra === 'A' && (nro === 4 || nro === 5);
  };

  const allItems = [...itemsProductos.map((it) => ({ ...it, tipoProveedor: 'C' as const })), ...itemsConceptos.map((it) => ({ ...it, tipoProveedor: 'G' as const }))];

  /** Paso 1: Subtotal Bruto = suma de TODOS los renglones (sin excluir exentos) */
  const subtotalBruto = allItems.reduce((sum, it) => sum + subtotalEfectivoItem(it), 0);

  /** Paso 2: Descuento (bonificación) aplicado sobre el Subtotal Bruto */
  const factorBonif = (1 - (Number(formulario.porcDesc) || 0) / 100) * (1 - (Number(formulario.porcDesc2) || 0) / 100) * (1 - (Number(formulario.porcDesc3) || 0) / 100);
  const totalBonificacion = Math.round(subtotalBruto * (1 - factorBonif) * 100) / 100;
  const subtotalNeto = Math.round((subtotalBruto - totalBonificacion) * 100) / 100;

  const computeTipoComprobante = (letraValue: string, esNota: boolean): string => {
    const L = (letraValue || '').trim().toUpperCase();
    const esFactura = !esNota;
    if (L === 'A') {
      return esFactura ? '01' : '02';
    }
    if (L === 'B') {
      return esFactura ? '06' : '07';
    }
    if (L === 'C') {
      return esFactura ? '11' : '12';
    }
    return '00';
  };

  useEffect(() => {
    const nuevo = computeTipoComprobante(formulario.letra || '', esNotaDebito);
    if (formulario.tipoComprobante !== nuevo) {
      setFormulario((prev) => ({ ...prev, tipoComprobante: nuevo }));
    }
  }, [formulario.letra, esNotaDebito, formulario.tipoComprobante]);
  const impuestosPorTasa = new Map<number, { tasa: number; monto: number }>();
  const calculaIva = letra !== 'B' && letra !== 'C' && letra !== 'X';

  /** Paso 3: Subtotal Gravado = solo ítems gravados (tasa1, 2, 3) con descuento. Si todos son exentos (tasa4/5), es cero. */
  const subtotalGravado = calculaIva
    ? Math.round(
        allItems.reduce((sum, it) => {
          if (it.tipoProveedor === 'G') {
            const hasGravado = (it.tasa1 ?? 0) > 0 || (it.tasa2 ?? 0) > 0 || (it.tasa3 ?? 0) > 0;
            if (!hasGravado) return sum;
            const subBrutoItem = subtotalEfectivoItem(it);
            const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
            return sum + subNetoItem;
          }
          const codigo = (it.codigoArticulo || '').trim();
          const prod = codigo ? productCache[codigo] : null;
          if (esExento(prod)) return sum;
          const subBrutoItem = subtotalEfectivoItem(it);
          const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
          return sum + subNetoItem;
        }, 0) * 100
      ) / 100
    : subtotalNeto;

  allItems.forEach((it) => {
    if (!calculaIva) return;

    const subBrutoItem = subtotalEfectivoItem(it);
    const subNetoItem = Math.round(subBrutoItem * factorBonif * 100) / 100;
    const codigo = (it.codigoArticulo || '').trim();
    const prod = codigo ? productCache[codigo] : null;

    if (it.tipoProveedor === 'G') {
      const t1 = it.tasa1 ?? 0, t2 = it.tasa2 ?? 0, t3 = it.tasa3 ?? 0, t4 = it.tasa4 ?? 0, t5 = it.tasa5 ?? 0;
      if (t1 > 0) {
        const tasa = impuestoTasas.impuestotasa1 ?? 21;
        if (impuestosPorTasa.has(tasa)) impuestosPorTasa.get(tasa)!.monto += t1;
        else impuestosPorTasa.set(tasa, { tasa, monto: t1 });
      }
      if (t2 > 0) {
        const tasa = impuestoTasas.impuestotasa2 ?? 10.5;
        if (impuestosPorTasa.has(tasa)) impuestosPorTasa.get(tasa)!.monto += t2;
        else impuestosPorTasa.set(tasa, { tasa, monto: t2 });
      }
      if (t3 > 0) {
        const tasa = impuestoTasas.impuestotasa3 ?? 27;
        if (impuestosPorTasa.has(tasa)) impuestosPorTasa.get(tasa)!.monto += t3;
        else impuestosPorTasa.set(tasa, { tasa, monto: t3 });
      }
      if (t4 > 0 || t5 > 0) {
        const montoExento = t4 + t5;
        if (impuestosPorTasa.has(0)) impuestosPorTasa.get(0)!.monto += montoExento;
        else impuestosPorTasa.set(0, { tasa: 0, monto: montoExento });
      }
      return;
    }

    if (esExento(prod)) {
      /** Paso 4: Monto exento = ítems exentos menos descuentos (se aplica factorBonif) */
      const montoExento = Math.round(subBrutoItem * factorBonif * 100) / 100;
      if (impuestosPorTasa.has(0)) {
        impuestosPorTasa.get(0)!.monto += montoExento;
      } else {
        impuestosPorTasa.set(0, { tasa: 0, monto: montoExento });
      }
      return;
    }

    const ivaPorc = getIvaPorcForLetra(letra, prod);
    const montoIVAItem = Math.round(subNetoItem * (ivaPorc / 100) * 100) / 100;
    if (ivaPorc > 0 && montoIVAItem > 0) {
      const tasa = ivaPorc;
      if (impuestosPorTasa.has(tasa)) {
        const exist = impuestosPorTasa.get(tasa)!;
        exist.monto += montoIVAItem;
      } else {
        impuestosPorTasa.set(tasa, { tasa, monto: montoIVAItem });
      }
    }
  });
  const impuestosArray = Array.from(impuestosPorTasa.values()).sort((a, b) => a.tasa - b.tasa);
  const montoIva = Math.round(impuestosArray.reduce((s, i) => s + i.monto, 0) * 100) / 100;
  const ivaEfectivo =
    calculaIva && ivaManualUsuario !== null
      ? Math.round(ivaManualUsuario * 100) / 100
      : montoIva;
  const totalImpuestosAdicionales = Math.round(impuestosAdicionales.reduce((s, i) => s + i.montoImpuesto, 0) * 100) / 100;
  // En letra C no calculamos impuestos en `impuestosPorTasa`, pero igualmente el usuario puede cargar
  // Exento/No Grav. (tasa4/tasa5). El backend guarda `Exento = Tasa4 + Tasa5`.
  const montoExentoLineas = Math.round(
    allItems.reduce((sum, it) => sum + (it.tasa4 ?? 0) + (it.tasa5 ?? 0), 0) * 100
  ) / 100;

  const totalFinal = Math.round(
    (subtotalGravado + ivaEfectivo + totalImpuestosAdicionales + (letra === 'C' ? montoExentoLineas : 0)) * 100
  ) / 100;

  const calcMontoImpuesto = (row: ImpuestoAdicionalRow, montoNeto: number): number => {
    const basadoen = row.basadoen ?? 1;
    const valor = row.valor ?? 0;
    let calc = 0;
    if (basadoen === 1 && valor !== 0) {
      calc = montoNeto * (valor / 100);
    } else if (basadoen === 2) {
      calc = valor;
    }
    if (row.montoGravMin != null && montoNeto < row.montoGravMin) return 0;
    if (row.montoGravMax != null && montoNeto > row.montoGravMax) calc = (row.montoGravMax ?? 0) * (valor / 100);
    if (row.montoImpuestoMin != null && calc < row.montoImpuestoMin) calc = row.montoImpuestoMin;
    if (row.montoImpuestoMax != null && calc > row.montoImpuestoMax) calc = row.montoImpuestoMax;
    return Math.round(calc * 100) / 100;
  };

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('COMPRAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
        ...paramsBloqueo,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) {
      loadProductos();
    }
  }, [openProductSearch, loadProductos]);

  const loadImpuestosSearch = useCallback(async () => {
    if (!openImpuestoSearch) return;
    try {
      const res = await impuestoService.getImpuestos({
        searchTerm: impuestoSearchTerm || undefined,
        pageNumber: impuestosSearchPage + 1,
        pageSize: 10
      });
      const list = res.items || [];
      setImpuestosSearchResults(list);
      setImpuestosSearchTotal(res.totalCount || 0);
    } catch {
      setImpuestosSearchResults([]);
      setImpuestosSearchTotal(0);
    }
  }, [openImpuestoSearch, impuestoSearchTerm, impuestosSearchPage]);

  useEffect(() => {
    if (openImpuestoSearch) loadImpuestosSearch();
  }, [openImpuestoSearch, loadImpuestosSearch]);

  useEffect(() => {
    if (compraPendienteNavAppliedRef.current) return;
    if (!compraPendienteFromNav) return;
    const rid = remitoIdParam ? parseInt(remitoIdParam, 10) : null;
    if (rid != null && !Number.isNaN(rid) && rid > 0) {
      compraPendienteNavAppliedRef.current = true;
      return;
    }

    compraPendienteNavAppliedRef.current = true;
    const p = compraPendienteFromNav;
    const np = p.nroProveedor;
    if (np && np > 0) {
      setFormulario((prev) => ({ ...prev, nroProveedor: np }));
      setProveedorNombre(p.nombreProveedor || '');
      void (async () => {
        try {
          const prov = await proveedorService.getProveedor(np);
          setProveedorNombre((prev) => prev || prov.nombre || '');
          setProveedorCuit(prov.cuit || '');
          setProveedorTipoIva((prov.tipoIva || '').trim().toUpperCase());
        } catch {
          /* se mantiene el nombre del pendiente */
        }
      })();
    }
    void (async () => {
      await agregarPendienteProductoRef.current(p);
      setAccordionProductosExpanded(true);
      setAccordionConceptosExpanded(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compraPendienteFromNav, remitoIdParam]);

  const handleSelectImpuesto = async (imp: any) => {
    const codigo = (imp.codigoImpuesto || imp.CodigoImpuesto || '').trim();
    if (!codigo) return;
    try {
      const detalle = await impuestoService.getImpuesto(codigo);
      const d = detalle as any;
      const cta = d.ctaCtable ?? d.CtaCtable;
      if (cta == null || cta === 0) {
        enqueueSnackbar('Este impuesto no tiene cuenta contable configurada', { variant: 'warning' });
        return;
      }
      const valor = d.valor ?? d.Valor ?? 0;
      const basadoen = d.basadoen ?? d.Basadoen ?? 1;
      const montoNeto = 0;
      const montoImpuesto = basadoen === 1 ? 0 : (typeof valor === 'number' ? valor : Number(valor) || 0);
      const newRow: ImpuestoAdicionalRow = {
        codigoImpuesto: codigo,
        ctaCtable: Number(cta),
        descripcion: (d.descripcion || d.Descripcion || codigo).trim(),
        montoNeto,
        montoImpuesto: Math.round(montoImpuesto * 100) / 100,
        valor: typeof valor === 'number' ? valor : Number(valor) || 0,
        basadoen,
        montoGravMin: d.montoGravMin ?? d.MontoGravMin,
        montoGravMax: d.montoGravMax ?? d.MontoGravMax,
        montoImpuestoMin: d.montoImpuestoMin ?? d.MontoImpuestoMin,
        montoImpuestoMax: d.montoImpuestoMax ?? d.MontoImpuestoMax,
        ctaCtableLabel: `${cta}`
      };
      setImpuestosAdicionales((prev) => [...prev, newRow]);
      setExpandOtrosImpuestos(true);
      setOpenImpuestoSearch(false);
      setImpuestoSearchTerm('');
      setImpuestosSearchPage(0);
    } catch {
      enqueueSnackbar('Error al cargar el impuesto', { variant: 'error' });
    }
  };

  const updateImpuestoAdicional = (idx: number, updates: Partial<ImpuestoAdicionalRow>) => {
    setImpuestosAdicionales((prev) => {
      const next = [...prev];
      const row = next[idx];
      if (!row) return prev;
      const merged = { ...row, ...updates };
      if ('montoNeto' in updates && updates.montoNeto !== undefined) {
        merged.montoImpuesto = calcMontoImpuesto(merged, merged.montoNeto);
      }
      next[idx] = merged;
      return next;
    });
  };

  const removeImpuestoAdicional = (idx: number) => {
    setImpuestosAdicionales((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleImputableSelectImpuesto = (imputable: Imputable) => {
    const idx = imputableSearchImpuestoIndex;
    if (idx !== null) {
      updateImpuestoAdicional(idx, {
        ctaCtable: imputable.nroCuenta,
        ctaCtableLabel: imputable.nomCuenta || imputable.descripcioncta || `${imputable.nroCuenta}`
      });
    }
    setOpenImputableSearchImpuesto(false);
    setImputableSearchImpuestoIndex(null);
  };

  const handleSelectImputableConcepto = (imputable: Imputable) => {
    const idx = imputableSearchConceptoIndex;
    if (idx !== null) {
      updateItemConcepto(idx, {
        codigoArticulo: String(imputable.nroCuenta),
        descripcion: imputable.nomCuenta || imputable.descripcioncta || '',
        nroCuenta: imputable.nroCuenta,
        tipoProveedor: 'G'
      });
    }
    setOpenImputableSearchConcepto(false);
    setImputableSearchConceptoIndex(null);
  };

  const handleSelectProduct = async (p: any) => {
    const idx = productSearchIndex;
    if (idx !== null) {
      const codigo = p.codigoArticulo || p.codigo || '';
      clearRechazoDiscontinuado('COMPRAS', codigo);
      if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis)) {
        enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
        return;
      }
      const decision = await evaluarUsoProductoDocumento(
        {
          codigoArticulo: codigo,
          tipoArticulo: p.tipoArticulo,
          descripcion: p.descripcion || p.descripcionArticulo,
        },
        'COMPRAS',
      );
      if (!decision.permitido) {
        if (decision.motivo !== 'discontinuado_rechazado') {
          enqueueSnackbar(decision.mensaje, { variant: 'warning' });
        }
        return;
      }
      const desc = p.descripcion || p.descripcionArticulo || '';
      const costo = Number(p.costo) || 0;
      let um = String(p.idUnidadMedida || p.um || '').trim();
      try {
        const umRes = await productoService.getUmCompra(String(codigo), formulario.nroProveedor || null);
        um = umRes.umCompra || um;
      } catch {
        /* ignore */
      }
      updateItemProducto(idx, { codigoArticulo: codigo, descripcion: desc, costoBrutoUnitario: costo, um });
      setProductCache((prev) => ({
        ...prev,
        [codigo]: {
          calculaIva: typeof p.calculaIva === 'string' ? p.calculaIva : undefined,
          ivaRI: p.ivaRI != null ? Number(p.ivaRI) : undefined,
          ivaCF: p.ivaCF != null ? Number(p.ivaCF) : undefined,
          ivaMT: p.ivaMT != null ? Number(p.ivaMT) : undefined,
          ivaRN: p.ivaRN != null ? Number(p.ivaRN) : undefined,
          nroTasaIva: p.nroTasaIva != null ? Number(p.nroTasaIva) : undefined
        }
      }));
    }
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const handleCodigoBlurOrEnter = useCallback(async (idx: number, codigoValue: string) => {
    const codigo = (codigoValue || '').trim();
    if (!codigo) return;
    try {
      const modo = (await paramsBusquedaProductoDocumento('COMPRAS')).modoBloqueoArtDiscontinuados ?? 1;
      setModoBloqueoDis(modo);
      const p = await cargarProductoParaDocumento(codigo, 'COMPRAS');
      if (esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modo)) {
        enqueueSnackbar(mensajeArticuloDiscontinuadoBloqueado(codigo), { variant: 'warning' });
        return;
      }
      const decision = await evaluarUsoProductoDocumento(
        {
          codigoArticulo: codigo,
          tipoArticulo: p.tipoArticulo,
          descripcion: p.descripcion || (p as any).descripcionArticulo,
        },
        'COMPRAS',
      );
      if (!decision.permitido) {
        if (decision.motivo === 'discontinuado_rechazado') {
          updateItemProducto(idx, { codigoArticulo: '', descripcion: '', costoBrutoUnitario: 0, um: '' });
        } else {
          enqueueSnackbar(decision.mensaje, { variant: 'warning' });
        }
        return;
      }
      const desc = p.descripcion || (p as any).descripcionArticulo || '';
      const costo = Number(p.costo) || Number((p as any).costoBrutoUnitario) || 0;
      let um = String((p as any).idUnidadMedida || (p as any).um || '').trim();
      try {
        const umRes = await productoService.getUmCompra(codigo, formulario.nroProveedor || null);
        um = umRes.umCompra || um;
      } catch {
        /* ignore */
      }
      updateItemProducto(idx, { codigoArticulo: codigo, descripcion: desc, costoBrutoUnitario: costo, um });
      const pr = p as any;
      setProductCache((prev) => ({
        ...prev,
        [codigo]: {
          calculaIva: typeof pr.calculaIva === 'string' ? pr.calculaIva : undefined,
          ivaRI: pr.ivaRI != null ? Number(pr.ivaRI) : undefined,
          ivaCF: pr.ivaCF != null ? Number(pr.ivaCF) : undefined,
          ivaMT: pr.ivaMT != null ? Number(pr.ivaMT) : undefined,
          ivaRN: pr.ivaRN != null ? Number(pr.ivaRN) : undefined,
          nroTasaIva: pr.nroTasaIva != null ? Number(pr.nroTasaIva) : undefined
        }
      }));
    } catch {
      enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
    }
  }, [enqueueSnackbar, formulario.nroProveedor]);

  const openBuscarPendientes = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setPendientesBusqueda('');
    setPendientesFechaDesde('');
    setPendientesFechaHasta('');
    setSoloInventariados(true);
    setOpenPendientes(true);
    setLoadingPendientes(true);
    try {
      const data = await comprasSeguimientoService.getPendientesMaster(formulario.nroProveedor, true);
      const rows = (data || []).filter((p) => (Number(p.pendienteFacturar) || 0) > 0);
      setPendientes(rows);
      if (rows.length === 0) {
        enqueueSnackbar('No hay líneas con pendiente de facturar para este proveedor', { variant: 'info' });
      }
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar pendientes'), { variant: 'error' });
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const agregarPendienteProducto = async (p: CompraPendienteMaster) => {
    let costoBruto = costoBrutoDesdePendienteMaster(p);
    const { porcDesc1, porcDesc2, porcDesc3, porcDesc4, porcDesc5 } = porcDescDesdePendienteMaster(p);
    if (costoBruto <= 0 && (p.codigoArticulo || '').trim()) {
      try {
        const pr = await cargarProductoParaDocumento((p.codigoArticulo || '').trim(), 'COMPRAS');
        costoBruto = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
      } catch {
        /* sin línea master ni artículo */
      }
    }
    const key = `${p.nroFormularioMaster}-${p.nroItemMaster}`;
    mastersEliminadosRef.current.delete(key);
    setItemsProductos((prev) => {
      const exists = prev.some(
        (it) =>
          it.nroFormularioMaster === p.nroFormularioMaster &&
          it.nroItemMaster === p.nroItemMaster
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          ...defaultItem,
          codigoArticulo: p.codigoArticulo || '',
          descripcion: p.descripcionArticulo || '',
          cantidad: cantidadSugeridaDesdePendiente(p),
          costoBrutoUnitario: costoBruto,
          porcDesc1,
          porcDesc2,
          porcDesc3,
          porcDesc4,
          porcDesc5,
          nroFormularioMaster: p.nroFormularioMaster,
          nroItemMaster: p.nroItemMaster,
        },
      ];
    });
  };
  agregarPendienteProductoRef.current = agregarPendienteProducto;

  const agregarTodosPendientes = async () => {
    if (agregandoTodosRef.current) return;
    if (!pendientesFiltrados.length) return;

    agregandoTodosRef.current = true;
    setAgregandoTodos(true);
    // Permite pintar el botón deshabilitado / "Agregando…" antes del trabajo pesado.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    const pendientesAAgregar = [...pendientesFiltrados];
    for (const p of pendientesAAgregar) {
      mastersEliminadosRef.current.delete(`${p.nroFormularioMaster}-${p.nroItemMaster}`);
    }

    const keysExistentes = new Set(
      itemsProductosRef.current.map((it) => `${it.nroFormularioMaster}-${it.nroItemMaster}`),
    );
    const nuevosItems: ItemProductoCompraDto[] = [];
    let agregadas = 0;
    let omitidas = 0;

    try {
      for (const p of pendientesAAgregar) {
        const key = `${p.nroFormularioMaster}-${p.nroItemMaster}`;
        if (keysExistentes.has(key)) {
          omitidas += 1;
          continue;
        }

        let costoBruto = costoBrutoDesdePendienteMaster(p);
        const { porcDesc1, porcDesc2, porcDesc3, porcDesc4, porcDesc5 } = porcDescDesdePendienteMaster(p);
        if (costoBruto <= 0 && (p.codigoArticulo || '').trim()) {
          try {
            const pr = await cargarProductoParaDocumento((p.codigoArticulo || '').trim(), 'COMPRAS');
            costoBruto = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
          } catch {
            /* sin línea master ni artículo */
          }
        }

        nuevosItems.push({
          ...defaultItem,
          codigoArticulo: p.codigoArticulo || '',
          descripcion: p.descripcionArticulo || '',
          cantidad: cantidadSugeridaDesdePendiente(p),
          costoBrutoUnitario: costoBruto,
          porcDesc1,
          porcDesc2,
          porcDesc3,
          porcDesc4,
          porcDesc5,
          nroFormularioMaster: p.nroFormularioMaster,
          nroItemMaster: p.nroItemMaster,
        });
        keysExistentes.add(key);
        agregadas += 1;
      }

      if (agregadas === 0) {
        enqueueSnackbar('No se pudo agregar ninguna línea desde pendientes', { variant: 'info' });
        return;
      }

      setItemsProductos((prev) => {
        const keys = new Set(prev.map((it) => `${it.nroFormularioMaster}-${it.nroItemMaster}`));
        const toAdd = nuevosItems.filter((it) => {
          const key = `${it.nroFormularioMaster}-${it.nroItemMaster}`;
          if (mastersEliminadosRef.current.has(key)) return false;
          if (keys.has(key)) return false;
          keys.add(key);
          return true;
        });
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
      setOpenPendientes(false);
      enqueueSnackbar(
        omitidas > 0
          ? `Se agregaron ${agregadas} líneas. ${omitidas} quedaron omitidas.`
          : `Se agregaron ${agregadas} líneas desde pendientes.`,
        { variant: 'success' },
      );
    } finally {
      agregandoTodosRef.current = false;
      setAgregandoTodos(false);
    }
  };

  const handleSubmit = async () => {
    if (!formulario.idCompania?.trim()) {
      enqueueSnackbar('Debe seleccionar una compañía', { variant: 'warning' });
      return;
    }
    if (!formulario.letra?.trim()) {
      enqueueSnackbar('La letra es requerida', { variant: 'warning' });
      return;
    }
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!proveedorNombre.trim()) {
      enqueueSnackbar('Debe ingresar el nombre del proveedor del comprobante', { variant: 'warning' });
      return;
    }
    if (!proveedorCuit.trim()) {
      enqueueSnackbar('Debe ingresar el CUIT del proveedor del comprobante', { variant: 'warning' });
      return;
    }
    if (!proveedorTipoIva.trim()) {
      enqueueSnackbar('Debe indicar el tipo de IVA del proveedor del comprobante', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe ingresar una sucursal válida', { variant: 'warning' });
      return;
    }
    if (!formulario.formaDePago?.trim()) {
      enqueueSnackbar('La forma de pago es requerida', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionDestino || formulario.nroUbicacionDestino <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación destino', { variant: 'warning' });
      return;
    }
    if (!formulario.tipoComprobante?.trim()) {
      enqueueSnackbar('El tipo de comprobante es requerido', { variant: 'warning' });
      return;
    }

    if (comprobanteDuplicado) {
      enqueueSnackbar(MSG_COMPROBANTE_DUPLICADO, {
        variant: 'error',
        autoHideDuration: 6000,
      });
      return;
    }

    const letraCheck = (formulario.letra ?? '').trim() || 'A';
    const nroCheck = formulario.nroComprobante ?? 0;
    if (nroCheck > 0) {
      try {
        const dup = await facturaCompraService.buscarDuplicadoPorComprobante({
          letra: letraCheck,
          nroSuc: formulario.nroSuc ?? 0,
          nroComprobante: nroCheck,
          cuit: proveedorCuit,
          esNotaDebito,
        });
        if (dup) {
          setComprobanteDuplicado(true);
          enqueueSnackbar(MSG_COMPROBANTE_DUPLICADO, {
            variant: 'error',
            autoHideDuration: 6000,
          });
          return;
        }
      } catch {
        // Si falla la consulta, el POST sigue y el backend devolverá el error como hasta ahora.
      }
    }

    const letraValue = formulario.letra?.trim().toUpperCase() || '';
    const itemsProductosValidos = itemsProductos.filter(
      (it) => (it.codigoArticulo || '').trim() && Number(it.cantidad) > 0 && Number(it.costoBrutoUnitario) > 0
    );
    // En letra C es común cargar "Exento/No Grav." (tasa4/tasa5) pero tener "Neto Grav/Unit" (costoBrutoUnitario) en 0.
    // El backend igual exige CostoBrutoUnitario > 0, así que aceptamos el ítem si tiene exento y luego derivamos el costo al armar el payload.
    const itemsConceptosValidos = itemsConceptos.filter((it) => {
      const hasCodigo = (it.codigoArticulo || '').trim().length > 0;
      const hasNroCuenta = (it.nroCuenta ?? 0) > 0;
      const qty = Number(it.cantidad) || 0;
      const costoUnit = Number(it.costoBrutoUnitario) || 0;
      const exentoTotal = (Number(it.tasa4) || 0) + (Number(it.tasa5) || 0);
      if (!hasCodigo || !hasNroCuenta || qty <= 0) return false;
      // Si está completo por costo unitario, ok.
      if (costoUnit > 0) return true;
      // Si es letra C y se cargó exento, aceptamos para luego derivar costo.
      return letraValue === 'C' && exentoTotal > 0;
    });
    const itemsValidos = [...itemsProductosValidos.map((it) => ({ ...it, tipoProveedor: 'C' as const })), ...itemsConceptosValidos.map((it) => ({ ...it, tipoProveedor: 'G' as const }))];

    // TEMPORAL: deshabilitar control de frontend.
    // Nota: el backend todavía valida que cada item tenga CostoBrutoUnitario > 0 y Cantidad > 0.
    // Este bloqueo evita que el usuario pueda enviar en casos donde la UI está completando
    // otros campos (p.ej. Exento/No Grav.) que no impactan en `costoBrutoUnitario`.
    // if (itemsValidos.length === 0) {
    //   enqueueSnackbar('Debe agregar al menos un ítem válido (producto o concepto) con código, cantidad y costo/neto mayor a cero', { variant: 'warning' });
    //   return;
    // }

    const factorBonif = (1 - (Number(formulario.porcDesc) || 0) / 100) * (1 - (Number(formulario.porcDesc2) || 0) / 100) * (1 - (Number(formulario.porcDesc3) || 0) / 100);
    const letraVal = formulario.letra?.trim().toUpperCase() || '';
    const tipoProveedorForm = itemsProductosValidos.length > 0 ? 'C' : 'G';

    const impuestosPayload = impuestosAdicionales
      .filter((ia) => ia.codigoImpuesto?.trim() && ia.ctaCtable != null && ia.ctaCtable > 0)
      .map((ia) => ({
        codigoImpuesto: ia.codigoImpuesto.trim(),
        ctaCtable: ia.ctaCtable,
        montoBaseImp: ia.montoNeto || undefined,
        montoImpuesto: ia.montoImpuesto || undefined
      }));

    const calculaIvaSubmit = letraValue !== 'B' && letraValue !== 'C';
    const ivaManualRounded =
      calculaIvaSubmit && ivaManualUsuario !== null ? Math.round(ivaManualUsuario * 100) / 100 : undefined;

    const payload: FacturaCompraCreatePayload = {
      formulario: {
        ...formulario,
        tipoProveedor: formulario.tipoProveedor || tipoProveedorForm,
        fechaEmision: formulario.fechaEmision || toLocalDateStringYMD(),
        fechaVencim: formulario.fechaVencim || undefined,
        aplicacionIva: formulario.aplicacionIva || formulario.fechaEmision || toLocalDateStringYMD(),
        nombre: proveedorNombre.trim().slice(0, 40),
        cuit: formatCuit(proveedorCuit).slice(0, 13),
        tipoIva: proveedorTipoIva.trim().toUpperCase().slice(0, 1),
        ...(ivaManualRounded !== undefined ? { ivaManual: ivaManualRounded } : {})
      },
      items: itemsValidos.map((it) => {
        const codigo = (it.codigoArticulo || '').trim();
        const esConcepto = it.tipoProveedor === 'G';
        if (esConcepto) {
          const qty = Number(it.cantidad) || 0;
          const exentoTotal = (Number(it.tasa4) || 0) + (Number(it.tasa5) || 0);
          const costoUnitRaw = Number(it.costoBrutoUnitario) || 0;
          // Backend exige CostoBrutoUnitario > 0. Si el usuario cargó el exento pero no el neto unit, lo derivamos desde (tasa4+tasa5)/cantidad.
          const costoUnitDerived =
            costoUnitRaw > 0
              ? costoUnitRaw
              : letraValue === 'C' && exentoTotal > 0 && qty > 0
                ? Math.round((exentoTotal / qty) * 100) / 100
                : 0;
          return {
            codigoArticulo: codigo,
            descripcion: it.descripcion?.trim(),
            cantidad: Number(it.cantidad) || 0,
            costoBrutoUnitario: costoUnitDerived,
            porcDesc1: 0,
            porcDesc2: 0,
            porcDesc3: 0,
            porcDesc4: 0,
            porcDesc5: 0,
            um: it.um?.trim() || 'UN',
            codigoMoneda: it.codigoMoneda,
            cotizacion: it.cotizacion,
            notas: normalizeItemNotas(it.notas),
            tasa1: Number(it.tasa1) || 0,
            tasa2: Number(it.tasa2) || 0,
            tasa3: Number(it.tasa3) || 0,
            tasa4: Number(it.tasa4) || 0,
            tasa5: Number(it.tasa5) || 0,
            impInterno1: 0,
            impInterno2: 0,
            tasaHidrica: 0,
            tipoProveedor: 'G' as const,
            nroCuenta: it.nroCuenta,
            nroFormularioMaster: it.nroFormularioMaster,
            nroItemMaster: it.nroItemMaster
          };
        }
        const prod = codigo ? productCache[codigo] : null;
        const tasas = computeTasasForItem(it, prod, letraVal, factorBonif);
        return {
          codigoArticulo: codigo,
          descripcion: it.descripcion?.trim(),
          cantidad: Number(it.cantidad) || 0,
          costoBrutoUnitario: Number(it.costoBrutoUnitario) || 0,
          porcDesc1: Number(it.porcDesc1) || 0,
          porcDesc2: Number(it.porcDesc2) || 0,
          porcDesc3: Number(it.porcDesc3) || 0,
          porcDesc4: Number(it.porcDesc4) || 0,
          porcDesc5: Number(it.porcDesc5) || 0,
          um: it.um?.trim(),
          codigoMoneda: it.codigoMoneda,
          cotizacion: it.cotizacion,
          notas: normalizeItemNotas(it.notas),
          tasa1: tasas.tasa1,
          tasa2: tasas.tasa2,
          tasa3: tasas.tasa3,
          tasa4: tasas.tasa4,
          tasa5: tasas.tasa5,
          impInterno1: 0,
          impInterno2: 0,
          tasaHidrica: 0,
          tipoProveedor: 'C' as const,
          nroFormularioMaster: it.nroFormularioMaster,
          nroItemMaster: it.nroItemMaster,
          ...(it.subtotalManual !== undefined
            ? { subtotalManual: Math.round(it.subtotalManual * 100) / 100 }
            : {}),
        };
      }),
      impuestos: impuestosPayload.length > 0 ? impuestosPayload : undefined
    };

    try {
      setSaving(true);
      const created = await facturaCompraService.createFacturaCompra(payload);
      if (remitoId && remitoOrigen) {
        try {
          const remitoPayload = buildRemitoUpdatePayload(remitoOrigen, created.nroFormulario);
          await remitoCompraService.updateRemitoCompra(remitoId, remitoPayload);
        } catch {
          enqueueSnackbar('Factura creada, pero no se pudo vincular el remito automáticamente', { variant: 'warning' });
        }
      }
      enqueueSnackbar('Factura de compra creada correctamente', { variant: 'success' });
      const hayProductos = !created.detalles || created.detalles.some(
        (d) => (d.tipoProveedor ?? '').trim().toUpperCase() !== 'G' && !!(d.codigoArticulo || '').trim(),
      );
      const nroProveedor = Number(created.nroProveedor ?? formulario.nroProveedor ?? 0);
      let hayCreditosPendientes = false;
      if (nroProveedor > 0) {
        try {
          const creditos =
            await tesoreriaAplicacionesService.getCreditosProveedorConSaldoPendiente(nroProveedor);
          hayCreditosPendientes = creditos.some(
            (c) => Math.round((c.saldoPendiente ?? 0) * 100) / 100 > 0
          );
        } catch {
          hayCreditosPendientes = false;
        }
      }
      if (hayCreditosPendientes) {
        setPostCreateNav({
          nroFormulario: created.nroFormulario,
          pedirEtiquetas: hayProductos,
        });
        setConfirmAplicarOpen(true);
      } else {
        navigate(`/compras/facturas/${created.nroFormulario}`, {
          state: { pedirEtiquetas: hayProductos },
        });
      }
    } catch (error: unknown) {
      const msg = getApiErrorMessage(error, 'Error al crear la factura de compra');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const handleIrAlDetalleSinAplicar = () => {
    const nav = postCreateNav;
    setConfirmAplicarOpen(false);
    setPostCreateNav(null);
    if (!nav) return;
    navigate(`/compras/facturas/${nav.nroFormulario}`, {
      state: { pedirEtiquetas: nav.pedirEtiquetas },
    });
  };

  const handleIrAlDetalleYAplicar = () => {
    const nav = postCreateNav;
    setConfirmAplicarOpen(false);
    setPostCreateNav(null);
    if (!nav) return;
    navigate(`/compras/facturas/${nav.nroFormulario}`, {
      state: { pedirEtiquetas: nav.pedirEtiquetas, abrirAplicarSaldo: true },
    });
  };

  const fechaEmisionStr = formulario.fechaEmision || toLocalDateStringYMD();
  const fechaVencimStr = formulario.fechaVencim || '';
  const fechaAplicacionIvaStr = formulario.aplicacionIva || formulario.fechaEmision || toLocalDateStringYMD();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent
          sx={{
            p: { xs: 2, sm: 3 },
            // (sin overrides de tipografía para mantener el tamaño por defecto)
          }}
          onKeyDown={handleEnterAsTabNavigation}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} flexWrap="wrap" useFlexGap spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/facturas')}
                sx={{ textTransform: 'none' }}
              >
                Facturas de Compra
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Nueva Factura de Compra
              </Typography>
            </Stack>
            {remitoId && (
              <Chip
                label={
                  remitoOrigen
                    ? (() => {
                        const nroComprobanteRemito = remitoOrigen.nroComprobante ?? remitoOrigen.nroRemito ?? '';
                        const nroComprobanteNum =
                          typeof nroComprobanteRemito === 'number'
                            ? nroComprobanteRemito
                            : parseInt(String(nroComprobanteRemito), 10);
                        const ok =
                          !!(remitoOrigen.letra || '').trim() &&
                          (remitoOrigen.nroSuc ?? 0) > 0 &&
                          Number.isFinite(nroComprobanteNum) &&
                          nroComprobanteNum > 0;

                        return ok
                          ? `Facturando remito ${formatoLetraSucNro(remitoOrigen.letra, remitoOrigen.nroSuc, nroComprobanteRemito)} (#${remitoId})`
                          : `Facturando remito #${remitoId}`;
                      })()
                    : `Facturando remito #${remitoId}`
                }
                color="info"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handlePrecargaPdfPick}
            >
              Precargar desde PDF
            </Button>
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(e) => void handlePrecargaPdfSelected(e)}
            />
          </Stack>

          <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                Compañía
              </Typography>
              <SearchableSelect
                label="Compañía"
                required
                value={formulario.idCompania || null}
                onChange={(v) => handleFormularioChange({ idCompania: v ?? '' })}
                options={companias.map((c) => ({
                  value: c.idCompania,
                  label: `${c.idCompania} - ${c.nombre}`,
                }))}
              />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                Proveedor
              </Typography>
              <ProveedorSelectorField
                label="Proveedor"
                required
                value={
                  formulario.nroProveedor > 0
                    ? {
                        nroProveedor: formulario.nroProveedor,
                        nombre: proveedorNombre || `Proveedor ${formulario.nroProveedor}`,
                        cuit: proveedorCuit || undefined,
                        tipoIva: proveedorTipoIva || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
                onEditDatosFiscales={() => setShowDatosProveedor((v) => !v)}
                onNuevoProveedor={() => setNuevoProveedorOpen(true)}
              />
            </Box>

            <Box>
          <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
            Detalle
          </Typography>
          {!formulario.nroProveedor || formulario.nroProveedor <= 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleccione un proveedor para poder cargar ítems.
            </Typography>
          ) : null}
          <Box sx={{ mb: 2, opacity: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 0.5 : 1, pointerEvents: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 'none' : 'auto' }}>
            <Accordion expanded={accordionProductosExpanded} onChange={(_, exp) => setAccordionProductosExpanded(!!exp)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                  '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#424242' }}>Items de Compras</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FacturaCompraArticulosSearchBar
                      nroProveedor={formulario.nroProveedor}
                      disabled={!formulario.nroProveedor || formulario.nroProveedor <= 0}
                      onAgregarProducto={handleAgregarProductoDesdeBusqueda}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => void openBuscarPendientes()}
                    disabled={!formulario.nroProveedor || formulario.nroProveedor <= 0}
                    fullWidth={isMobile}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    Buscar pendientes
                  </Button>
                </Box>
                {isMobile ? (
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    {itemsProductos.length === 0 ? (
                      <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                        Sin ítems de producto. Usá la búsqueda de artículos o Agregar línea.
                      </Typography>
                    ) : (
                      itemsProductos.map((item, idx) => (
                        <Card key={`p-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack spacing={1.25}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Producto {idx + 1}
                                </Typography>
                                <IconButton size="small" onClick={() => removeItemProducto(idx)} color="error" aria-label="Eliminar producto">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                              <TextField
                                size="small"
                                fullWidth
                                label="Código"
                                value={item.codigoArticulo || ''}
                                onChange={(e) => {
                                  clearRechazoDiscontinuado('COMPRAS', item.codigoArticulo);
                                  clearRechazoDiscontinuado('COMPRAS', e.target.value);
                                  updateItemProducto(idx, { codigoArticulo: e.target.value });
                                }}
                                onBlur={(e) => handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                                  }
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setProductSearchIndex(idx);
                                          setOpenProductSearch(true);
                                        }}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <TextField
                                size="small"
                                fullWidth
                                label="Descripción"
                                multiline
                                minRows={1}
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemProducto(idx, { descripcion: e.target.value })}
                              />
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="Cant."
                                    fullWidth
                                    value={item.cantidad ?? 1}
                                    onChange={(v) => updateItemProducto(idx, { cantidad: v })}
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="Costo unit."
                                    fullWidth
                                    value={item.costoBrutoUnitario ?? 0}
                                    onChange={(v) => updateItemProducto(idx, { costoBrutoUnitario: v })}
                                    inputProps={{ min: 0, step: 1 }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="% Desc 1"
                                    fullWidth
                                    value={item.porcDesc1 ?? 0}
                                    onChange={(v) => updateItemProducto(idx, { porcDesc1: v })}
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CurrencyTextField
                                      label="Subtotal"
                                      fullWidth
                                      value={subtotalEfectivoItem(item)}
                                      onChange={(v) => updateItemProducto(idx, { subtotalManual: v })}
                                      sx={
                                        item.subtotalManual !== undefined
                                          ? { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }
                                          : undefined
                                      }
                                    />
                                    {item.subtotalManual !== undefined && (
                                      <Tooltip title="Usar subtotal calculado">
                                        <IconButton
                                          size="small"
                                          onClick={() => updateItemProducto(idx, { subtotalManual: undefined })}
                                          aria-label="Usar subtotal calculado"
                                        >
                                          <RestartAltIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </Grid>
                              </Grid>
                              <ItemNotasTextField
                                fullWidth
                                label="Notas"
                                value={item.notas ?? ''}
                                maxLength={ITEM_NOTAS_MAX}
                                onChange={(notas) => updateItemProducto(idx, { notas })}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Stack>
                ) : (
                <TableContainer sx={itemsGridTableSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell>Código</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Cant.</TableCell>
                        <TableCell>UM</TableCell>
                        <TableCell align="right">Costo unit.</TableCell>
                        <TableCell align="right">% Desc 1</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                        <TableCell align="center" />
                        <TableCell>Notas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemsProductos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                            Sin ítems de producto. Usá la búsqueda de artículos o Agregar línea.
                          </TableCell>
                        </TableRow>
                      ) : (
                      itemsProductos.map((item, idx) => (
                        <TableRow key={`p-${idx}`}>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.codigoArticulo || ''}
                              onChange={(e) => {
                                clearRechazoDiscontinuado('COMPRAS', item.codigoArticulo);
                                clearRechazoDiscontinuado('COMPRAS', e.target.value);
                                updateItemProducto(idx, { codigoArticulo: e.target.value });
                              }}
                              onBlur={(e) => handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                                }
                              }}
                              placeholder="Código"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setProductSearchIndex(idx);
                                        setOpenProductSearch(true);
                                      }}
                                    >
                                      <SearchIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                              sx={{ minWidth: 96 }}
                            />
                          </TableCell>
                            <TableCell sx={{ minWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemProducto(idx, { descripcion: e.target.value })}
                                placeholder="Descripción"
                                sx={{
                                  '& .MuiInputBase-inputMultiline': {
                                    overflow: 'hidden',
                                  },
                                }}
                              />
                            </TableCell>
                          <TableCell align="right" sx={{ width: 72 }}>
                            <DecimalTextField
                              value={item.cantidad ?? 1}
                              onChange={(v) => updateItemProducto(idx, { cantidad: v })}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              sx={{ width: 64 }}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 64 }}>
                            <TextField
                              size="small"
                              value={item.um || ''}
                              onChange={(e) => updateItemProducto(idx, { um: e.target.value })}
                              sx={{ width: 56 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 96 }}>
                            <DecimalTextField
                              value={item.costoBrutoUnitario ?? 0}
                              onChange={(v) => updateItemProducto(idx, { costoBrutoUnitario: v })}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{ width: 96 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 64 }}>
                            <DecimalTextField
                              value={item.porcDesc1 ?? 0}
                              onChange={(v) => updateItemProducto(idx, { porcDesc1: v })}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              sx={{ width: 56 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ width: 112, minWidth: 112 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                              <CurrencyTextField
                                value={subtotalEfectivoItem(item)}
                                onChange={(v) => updateItemProducto(idx, { subtotalManual: v })}
                                sx={{
                                  width: 100,
                                  ...(item.subtotalManual !== undefined
                                    ? { '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }
                                    : {}),
                                }}
                              />
                              {item.subtotalManual !== undefined && (
                                <Tooltip title="Usar subtotal calculado">
                                  <IconButton
                                    size="small"
                                    onClick={() => updateItemProducto(idx, { subtotalManual: undefined })}
                                    aria-label="Usar subtotal calculado"
                                  >
                                    <RestartAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center" sx={{ width: 40 }}>
                            <IconButton size="small" onClick={() => removeItemProducto(idx)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            <ItemNotasTextField
                              fullWidth
                              value={item.notas ?? ''}
                              maxLength={ITEM_NOTAS_MAX}
                              placeholder="Notas"
                              onChange={(notas) => updateItemProducto(idx, { notas })}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
                <Box mt={1} display="flex" justifyContent="flex-start" alignItems="center">
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addItemProducto}
                    variant="text"
                  >
                    Agregar línea
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={accordionConceptosExpanded} onChange={(_, exp) => setAccordionConceptosExpanded(!!exp)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                  '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#424242' }}>Items de Gastos</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {isMobile ? (
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    {itemsConceptos.length === 0 ? (
                      <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                        Sin ítems de concepto. Use el botón Agregar concepto para añadir.
                      </Typography>
                    ) : (
                      itemsConceptos.map((item, idx) => (
                        <Card key={`c-${idx}`} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack spacing={1.25}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Concepto {idx + 1}
                                </Typography>
                                <IconButton size="small" onClick={() => removeItemConcepto(idx)} color="error" aria-label="Eliminar concepto">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                              <TextField
                                size="small"
                                fullWidth
                                label="Concepto"
                                value={item.codigoArticulo || ''}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setImputableSearchConceptoIndex(idx);
                                          setOpenImputableSearchConcepto(true);
                                        }}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                onClick={() => {
                                  setImputableSearchConceptoIndex(idx);
                                  setOpenImputableSearchConcepto(true);
                                }}
                              />
                              <TextField
                                size="small"
                                fullWidth
                                label="Descripción"
                                multiline
                                minRows={1}
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemConcepto(idx, { descripcion: e.target.value })}
                              />
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="Cant."
                                    fullWidth
                                    value={item.cantidad ?? 1}
                                    onChange={(qty) => {
                                      const netoUnit = item.costoBrutoUnitario ?? 0;
                                      const netoTotal = qty * netoUnit;
                                      const letraVal = formulario.letra?.trim().toUpperCase() || '';
                                      const porcTasa1 = impuestoTasas.impuestotasa1 ?? 21;
                                      const updates: Partial<CreateItemCompraDto> = { cantidad: qty };
                                      if (letraVal === 'A' && netoTotal > 0) {
                                        updates.tasa1 = Math.round(netoTotal * (porcTasa1 / 100) * 100) / 100;
                                        updates.tasa2 = 0;
                                        updates.tasa3 = 0;
                                        updates.tasa4 = 0;
                                        updates.tasa5 = 0;
                                      }
                                      updateItemConcepto(idx, updates);
                                    }}
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                  />
                                </Grid>
                                {!esLetraC && (
                                  <Grid item xs={6}>
                                    <DecimalTextField
                                      label="Neto Grav/Unit"
                                      fullWidth
                                      value={item.costoBrutoUnitario ?? 0}
                                      onChange={(netoUnit) => {
                                        const qty = item.cantidad ?? 1;
                                        const netoTotal = qty * netoUnit;
                                        const letraVal = formulario.letra?.trim().toUpperCase() || '';
                                        const porcTasa1 = impuestoTasas.impuestotasa1 ?? 21;
                                        const updates: Partial<CreateItemCompraDto> = { costoBrutoUnitario: netoUnit };
                                        if (letraVal === 'A' && netoTotal > 0) {
                                          updates.tasa1 = Math.round(netoTotal * (porcTasa1 / 100) * 100) / 100;
                                          updates.tasa2 = 0;
                                          updates.tasa3 = 0;
                                          updates.tasa4 = 0;
                                          updates.tasa5 = 0;
                                        }
                                        updateItemConcepto(idx, updates);
                                      }}
                                      inputProps={{ min: 0, step: 0.01 }}
                                      placeholder="0"
                                    />
                                  </Grid>
                                )}
                                {!esLetraC && (
                                  <>
                                    <Grid item xs={6}>
                                      <DecimalTextField
                                        label={`IVA ${impuestoTasas.impuestotasa1 ?? 21}%`}
                                        fullWidth
                                        value={item.tasa1 ?? 0}
                                        onChange={(v) => updateItemConcepto(idx, { tasa1: v, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: 0 })}
                                        inputProps={{ min: 0, step: 0.01 }}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <DecimalTextField
                                        label={`IVA ${impuestoTasas.impuestotasa2 ?? 10.5}%`}
                                        fullWidth
                                        value={item.tasa2 ?? 0}
                                        onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: v, tasa3: 0, tasa4: 0, tasa5: 0 })}
                                        inputProps={{ min: 0, step: 0.01 }}
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <DecimalTextField
                                        label={`IVA ${impuestoTasas.impuestotasa3 ?? 27}%`}
                                        fullWidth
                                        value={item.tasa3 ?? 0}
                                        onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: 0, tasa3: v, tasa4: 0, tasa5: 0 })}
                                        inputProps={{ min: 0, step: 0.01 }}
                                      />
                                    </Grid>
                                  </>
                                )}
                                <Grid item xs={6}>
                                  <DecimalTextField
                                    label="Exento/No Grav."
                                    fullWidth
                                    value={(item.tasa4 ?? 0) + (item.tasa5 ?? 0)}
                                    onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: v })}
                                    inputProps={{ min: 0, step: 0.01 }}
                                  />
                                </Grid>
                              </Grid>
                              <ItemNotasTextField
                                fullWidth
                                label="Notas"
                                value={item.notas ?? ''}
                                maxLength={ITEM_NOTAS_MAX}
                                onChange={(notas) => updateItemConcepto(idx, { notas })}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Stack>
                ) : (
                <TableContainer sx={itemsGridTableSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell>Concepto</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Cant.</TableCell>
                        {!esLetraC && (
                          <TableCell align="right">Neto Grav/Unit</TableCell>
                        )}
                        {!esLetraC && (
                          <>
                            <TableCell align="right">IVA {impuestoTasas.impuestotasa1 ?? 21}%</TableCell>
                            <TableCell align="right">IVA {impuestoTasas.impuestotasa2 ?? 10.5}%</TableCell>
                            <TableCell align="right">IVA {impuestoTasas.impuestotasa3 ?? 27}%</TableCell>
                          </>
                        )}
                        <TableCell align="right">Exento/No Grav.</TableCell>
                        <TableCell align="center" />
                        <TableCell>Notas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemsConceptos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columnasConceptos} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                            Sin ítems de concepto. Use el botón Agregar concepto para añadir.
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsConceptos.map((item, idx) => (
                          <TableRow key={`c-${idx}`}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={item.codigoArticulo || ''}
                                placeholder="Concepto"
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setImputableSearchConceptoIndex(idx);
                                          setOpenImputableSearchConcepto(true);
                                        }}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                onClick={() => {
                                  setImputableSearchConceptoIndex(idx);
                                  setOpenImputableSearchConcepto(true);
                                }}
                                sx={{ minWidth: 120 }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemConcepto(idx, { descripcion: e.target.value })}
                                placeholder="Descripción"
                                sx={{
                                  '& .MuiInputBase-inputMultiline': {
                                    overflow: 'hidden',
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 64 }}>
                              <DecimalTextField
                                value={item.cantidad ?? 1}
                                onChange={(qty) => {
                                  const netoUnit = item.costoBrutoUnitario ?? 0;
                                  const netoTotal = qty * netoUnit;
                                  const letraVal = formulario.letra?.trim().toUpperCase() || '';
                                  const porcTasa1 = impuestoTasas.impuestotasa1 ?? 21;
                                  const updates: Partial<CreateItemCompraDto> = { cantidad: qty };
                                  if (letraVal === 'A' && netoTotal > 0) {
                                    updates.tasa1 = Math.round(netoTotal * (porcTasa1 / 100) * 100) / 100;
                                    updates.tasa2 = 0;
                                    updates.tasa3 = 0;
                                    updates.tasa4 = 0;
                                    updates.tasa5 = 0;
                                  }
                                  updateItemConcepto(idx, updates);
                                }}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                sx={{ width: 56 }}
                              />
                            </TableCell>
                            {!esLetraC && (
                              <TableCell align="right" sx={{ width: 96 }}>
                                <DecimalTextField
                                  value={item.costoBrutoUnitario ?? 0}
                                  onChange={(netoUnit) => {
                                    const qty = item.cantidad ?? 1;
                                    const netoTotal = qty * netoUnit;
                                    const letraVal = formulario.letra?.trim().toUpperCase() || '';
                                    const porcTasa1 = impuestoTasas.impuestotasa1 ?? 21;
                                    const updates: Partial<CreateItemCompraDto> = { costoBrutoUnitario: netoUnit };
                                    if (letraVal === 'A' && netoTotal > 0) {
                                      updates.tasa1 = Math.round(netoTotal * (porcTasa1 / 100) * 100) / 100;
                                      updates.tasa2 = 0;
                                      updates.tasa3 = 0;
                                      updates.tasa4 = 0;
                                      updates.tasa5 = 0;
                                    }
                                    updateItemConcepto(idx, updates);
                                  }}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  placeholder="0"
                                  sx={{ width: 88 }}
                                />
                              </TableCell>
                            )}
                            {!esLetraC && (
                              <>
                                <TableCell align="right" sx={{ width: 72 }}>
                                  <DecimalTextField
                                    value={item.tasa1 ?? 0}
                                    onChange={(v) => updateItemConcepto(idx, { tasa1: v, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: 0 })}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{ width: 64 }}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ width: 72 }}>
                                  <DecimalTextField
                                    value={item.tasa2 ?? 0}
                                    onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: v, tasa3: 0, tasa4: 0, tasa5: 0 })}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{ width: 64 }}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ width: 72 }}>
                                  <DecimalTextField
                                    value={item.tasa3 ?? 0}
                                    onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: 0, tasa3: v, tasa4: 0, tasa5: 0 })}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    sx={{ width: 64 }}
                                  />
                                </TableCell>
                              </>
                            )}
                            <TableCell align="right" sx={{ width: 88 }}>
                              <DecimalTextField
                                value={(item.tasa4 ?? 0) + (item.tasa5 ?? 0)}
                                // En letra C cargamos "Exento/No Grav." en tasa5 (dejando tasa4 en 0).
                                onChange={(v) => updateItemConcepto(idx, { tasa1: 0, tasa2: 0, tasa3: 0, tasa4: 0, tasa5: v })}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ width: 40 }}>
                              <IconButton size="small" onClick={() => removeItemConcepto(idx)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ minWidth: 110 }}>
                              <ItemNotasTextField
                                fullWidth
                                value={item.notas ?? ''}
                                maxLength={ITEM_NOTAS_MAX}
                                placeholder="Notas"
                                onChange={(notas) => updateItemConcepto(idx, { notas })}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
                <Box mt={1} display="flex" justifyContent="flex-start" alignItems="center">
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addItemConcepto}
                    variant="text"
                  >
                    Agregar concepto
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>

          <ImputableSearch
            open={openImputableSearchConcepto}
            onClose={() => { setOpenImputableSearchConcepto(false); setImputableSearchConceptoIndex(null); }}
            onSelect={handleSelectImputableConcepto}
            tipoMovimientoIE="E"
          />
            </Box>
          </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Accordion
                expanded={showDatosEmision}
                onChange={(_, exp) => setShowDatosEmision(!!exp)}
                variant="outlined"
                disableGutters
                sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2">Datos de emisión</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Box sx={{ width: 72, flexShrink: 0 }}>
                        <SearchableSelect
                          label="Letra"
                          required
                          size="small"
                          value={formulario.letra || 'A'}
                          onChange={(v) => handleFormularioChange({ letra: v ?? 'A' })}
                          options={[
                            { value: 'A', label: 'A' },
                            { value: 'C', label: 'C' },
                            { value: 'X', label: 'X' },
                          ]}
                        />
                      </Box>
                      <TextField
                        label="Sucursal"
                        size="small"
                        sx={{ width: 88, flexShrink: 0 }}
                        value={String(formulario.nroSuc ?? 0)}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                          if (v === '') {
                            setNroSucFormulario(0);
                            return;
                          }
                          const n = parseInt(v, 10);
                          if (Number.isNaN(n)) return;
                          const next = n === 0 ? 0 : Math.min(99999, Math.max(1, n));
                          setNroSucFormulario(next);
                        }}
                        inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric', maxLength: 5 }}
                      />
                      <TextField
                        label="Nro."
                        size="small"
                        fullWidth
                        value={String(formulario.nroComprobante ?? 0)}
                        error={comprobanteDuplicado}
                        helperText={comprobanteDuplicado ? 'Ya existe para este CUIT' : undefined}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                          if (v === '') {
                            handleFormularioChange({ nroComprobante: 0 });
                            return;
                          }
                          const n = parseInt(v, 10);
                          if (Number.isNaN(n)) return;
                          const next = n === 0 ? 0 : Math.min(99999999, Math.max(1, n));
                          handleFormularioChange({ nroComprobante: next });
                        }}
                        inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric', maxLength: 8 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Tipo comprobante"
                        size="small"
                        value={formulario.tipoComprobante || ''}
                        InputProps={{ readOnly: true }}
                        placeholder="Se calcula según letra"
                        required
                        sx={{ width: '60%', flexShrink: 0 }}
                      />
                      <FormControlLabel
                        sx={{
                          m: 0,
                          width: '40%',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          justifyContent: 'center',
                        }}
                        control={
                          <Switch
                            checked={esNotaDebito}
                            onChange={(e) => setEsNotaDebito(e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        }
                        label="ND"
                      />
                    </Stack>
                    <TextField
                      label="Fecha de emisión"
                      type="date"
                      size="small"
                      fullWidth
                      value={fechaEmisionStr}
                      onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Fecha de vencimiento"
                      type="date"
                      size="small"
                      fullWidth
                      value={fechaVencimStr}
                      onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Fecha de aplicación IVA"
                      type="date"
                      size="small"
                      fullWidth
                      value={fechaAplicacionIvaStr}
                      onChange={(e) => handleFormularioChange({ aplicacionIva: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={showDatosProveedor}
                onChange={(_, exp) => setShowDatosProveedor(!!exp)}
                variant="outlined"
                disableGutters
                sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonOutlineIcon fontSize="small" color="action" />
                    <Typography variant="body2">Datos del proveedor</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Nombre"
                      size="small"
                      fullWidth
                      required
                      value={proveedorNombre}
                      onChange={(e) => setProveedorNombre(e.target.value.slice(0, 40))}
                      disabled={!formulario.nroProveedor || formulario.nroProveedor <= 0}
                      inputProps={{ maxLength: 40 }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="CUIT"
                      size="small"
                      fullWidth
                      required
                      value={proveedorCuit}
                      onChange={(e) => {
                        const digits = normalizeCuitForStorage(e.target.value);
                        setProveedorCuit(digits.length === 0 ? '' : formatCuit(digits));
                      }}
                      disabled={!formulario.nroProveedor || formulario.nroProveedor <= 0}
                      inputProps={{ maxLength: 13, inputMode: 'numeric' }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <SearchableSelect
                      label="Tipo IVA"
                      required
                      size="small"
                      disabled={!formulario.nroProveedor || formulario.nroProveedor <= 0}
                      value={proveedorTipoIva || null}
                      onChange={handleProveedorTipoIvaChange}
                      options={[
                        { value: 'I', label: 'Responsable Inscripto' },
                        { value: 'M', label: 'Monotributo' },
                        { value: 'E', label: 'Exento' },
                        { value: 'P', label: 'Presupuestos' },
                      ]}
                    />
                    <SearchableSelect
                      label="Tipo proveedor"
                      required
                      size="small"
                      value={formulario.tipoProveedor || 'C'}
                      onChange={(v) => handleFormularioChange({ tipoProveedor: v ?? 'C' })}
                      options={[
                        { value: 'C', label: 'Compras (C)' },
                        { value: 'G', label: 'Gastos (G)' },
                      ]}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={showDatosComerciales}
                onChange={(_, exp) => setShowDatosComerciales(!!exp)}
                variant="outlined"
                disableGutters
                sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AssignmentIndIcon fontSize="small" color="action" />
                    <Typography variant="body2">Datos comerciales</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={1.5}>
                    <SearchableSelect
                      label="Forma de pago"
                      required
                      size="small"
                      value={formulario.formaDePago || 'CONTADO'}
                      onChange={(v) => handleFormularioChange({ formaDePago: v ?? 'CONTADO' })}
                      options={FORMAS_DE_PAGO.map((fp) => ({ value: fp, label: fp }))}
                    />
                    <TextField
                      label="Ubicación destino"
                      size="small"
                      fullWidth
                      required
                      value={ubicacionNombre || ''}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setOpenUbicacionSearch(true)} edge="end" size="small">
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onClick={() => setOpenUbicacionSearch(true)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={accordionTotalesExpanded}
                onChange={(_, exp) => setAccordionTotalesExpanded(!!exp)}
                variant="outlined"
                disableGutters
                sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 'unset',
                    py: 0.75,
                    '& .MuiAccordionSummary-content': { my: 0.25, alignItems: 'flex-start' },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ width: '100%', pr: 0.5, minWidth: 0, flex: 1 }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
                      <CalculateOutlinedIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: 0.15 }} />
                      <Typography variant="body2" lineHeight={1.2} component="div">
                        Totales del
                        <br />
                        documento
                      </Typography>
                    </Stack>
                    {totalFinal > 0 && (
                      <Stack spacing={0.125} alignItems="flex-end" sx={{ flexShrink: 0, textAlign: 'right' }}>
                        {calculaIva && ivaEfectivo > 0 && (
                          <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                            IVA {formatCurrency(ivaEfectivo)}
                          </Typography>
                        )}
                        <Typography variant="body2" fontWeight={700} color="primary.main" lineHeight={1.25}>
                          {formatCurrency(totalFinal)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Subtotal Bruto</Typography>
                    <Typography variant="body2" fontWeight={700}>{formatCurrency(subtotalBruto)}</Typography>
                  </Box>

                  <Box sx={{ mt: 1.5, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                      Bonificación aplicada
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <DecimalTextField
                        value={formulario.porcDesc ?? 0}
                        onChange={(v) => handleFormularioChange({ porcDesc: v })}
                        sx={{ width: 72 }}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                      <Typography variant="caption" color="text.secondary">%</Typography>
                      <DecimalTextField
                        value={formulario.porcDesc2 ?? 0}
                        onChange={(v) => handleFormularioChange({ porcDesc2: v })}
                        sx={{ width: 72 }}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                      <Typography variant="caption" color="text.secondary">%</Typography>
                      <DecimalTextField
                        value={formulario.porcDesc3 ?? 0}
                        onChange={(v) => handleFormularioChange({ porcDesc3: v })}
                        sx={{ width: 72 }}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                      <Typography variant="caption" color="text.secondary">%</Typography>
                    </Stack>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Total bonificación</Typography>
                      <Typography variant="body2" fontWeight={700}>{formatCurrency(totalBonificacion)}</Typography>
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {letra === 'A' ? 'Subtotal Gravado' : 'Subtotal Neto'}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {formatCurrency(letra === 'A' ? subtotalGravado : subtotalNeto)}
                    </Typography>
                  </Box>

                  {calculaIva ? (
                    <Box sx={{ mb: 1 }}>
                      {impuestosArray.map((imp) => (
                        <Box key={imp.tasa} display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {imp.tasa === 0
                              ? 'IVA Exento'
                              : `IVA ${imp.tasa % 1 === 0 ? imp.tasa.toFixed(0) : imp.tasa.toFixed(1)}%`}
                          </Typography>
                          <Typography variant="body2">{formatCurrency(imp.monto)}</Typography>
                        </Box>
                      ))}
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          IVA total
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CurrencyTextField
                            value={ivaEfectivo}
                            onChange={(v) => setIvaManualUsuario(v)}
                            sx={{ width: 120 }}
                          />
                          {ivaManualUsuario !== null && (
                            <Button size="small" variant="text" onClick={() => setIvaManualUsuario(null)}>
                              Calculado
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  ) : letra === 'C' ? (
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Exento</Typography>
                      <Typography variant="body2">{formatCurrency(montoExentoLineas)}</Typography>
                    </Box>
                  ) : letra === 'X' ? (
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">IVA No categorizado</Typography>
                      <Typography variant="body2">{formatCurrency(subtotalNeto)}</Typography>
                    </Box>
                  ) : (
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">IVA</Typography>
                      <Typography variant="body2">{formatCurrency(0)}</Typography>
                    </Box>
                  )}

                  <Accordion
                    expanded={expandOtrosImpuestos}
                    onChange={(_, exp) => setExpandOtrosImpuestos(exp)}
                    sx={{ mt: 1, boxShadow: 'none', border: 'none', bgcolor: 'transparent', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        flexDirection: 'row-reverse',
                        minHeight: 36,
                        px: 0,
                        '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                        '& .MuiAccordionSummary-content': { margin: 0 },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">Otros impuestos</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatCurrency(totalImpuestosAdicionales)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Stack spacing={1.5} sx={{ mb: 1 }}>
                        {impuestosAdicionales.map((ia, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'grey.50',
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight={700}>
                                {ia.codigoImpuesto || 'Impuesto'}
                              </Typography>
                              <IconButton size="small" onClick={() => removeImpuestoAdicional(idx)} color="error">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            <Stack spacing={1}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <TextField
                                  size="small"
                                  label="Cta Ctable"
                                  type="number"
                                  value={ia.ctaCtable || ''}
                                  onChange={(e) =>
                                    updateImpuestoAdicional(idx, {
                                      ctaCtable: e.target.value === '' ? 0 : Number(e.target.value),
                                    })
                                  }
                                  fullWidth
                                  inputProps={{ style: { textAlign: 'right' } }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setImputableSearchImpuestoIndex(idx);
                                    setOpenImputableSearchImpuesto(true);
                                  }}
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <TextField
                                size="small"
                                label="Descripción"
                                fullWidth
                                value={ia.descripcion}
                                onChange={(e) => updateImpuestoAdicional(idx, { descripcion: e.target.value })}
                              />
                              <Stack direction="row" spacing={1}>
                                <DecimalTextField
                                  label="Neto"
                                  value={ia.montoNeto ?? 0}
                                  onChange={(v) => updateImpuestoAdicional(idx, { montoNeto: v })}
                                  sx={{ flex: 1 }}
                                  inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                                />
                                <DecimalTextField
                                  label="Impuesto"
                                  value={ia.montoImpuesto ?? 0}
                                  onChange={(v) => updateImpuestoAdicional(idx, { montoImpuesto: v })}
                                  sx={{ flex: 1, fontWeight: 600 }}
                                  inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                                />
                              </Stack>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setOpenImpuestoSearch(true)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                      >
                        Agregar impuesto
                      </Button>
                    </AccordionDetails>
                  </Accordion>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                  >
                    <Typography variant="subtitle2" fontWeight={800}>
                      TOTAL
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={900} color="primary.main">
                      {formatCurrency(totalFinal)}
                    </Typography>
                  </Box>
                  <ObservacionesConNotaProveedor nroProveedor={formulario.nroProveedor}>
                    <TextField
                      label="Observaciones"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={formulario.descripcion || ''}
                      onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ mt: 1.5 }}
                    />
                  </ObservacionesConNotaProveedor>
                </AccordionDetails>
              </Accordion>

              <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={saving || !!comprobanteDuplicado}
                  sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Guardar factura'}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/compras/facturas')}
                  disabled={saving}
                >
                  Cancelar
                </Button>
              </Stack>
              <NumeroInternoMeta nro={0} />
            </Box>
          </Grid>
          </Grid>
        </CardContent>
      </Card>

      <PrecargaFacturaCompraPdfDialog
        open={precargaDialogOpen}
        loading={precargaLoading}
        precarga={precargaResult}
        onClose={() => setPrecargaDialogOpen(false)}
        onApply={() => void handleApplyPrecarga()}
        onProveedorCreado={handleProveedorCreadoDesdePrecarga}
      />
      <NuevoProveedorDesdePrecargaDialog
        open={nuevoProveedorOpen}
        initialData={NUEVO_PROVEEDOR_VACIO}
        onClose={() => setNuevoProveedorOpen(false)}
        onCreated={handleProveedorCreadoNuevo}
      />
      <UbicacionSearch
        open={openUbicacionSearch}
        onClose={() => setOpenUbicacionSearch(false)}
        onSelect={handleUbicacionSelect}
      />

      <ImputableSearch
        open={openImputableSearchImpuesto}
        onClose={() => { setOpenImputableSearchImpuesto(false); setImputableSearchImpuestoIndex(null); }}
        onSelect={handleImputableSelectImpuesto}
        tipoMovimientoIE="E"
      />

      <Dialog open={openImpuestoSearch} onClose={() => { setOpenImpuestoSearch(false); setImpuestoSearchTerm(''); setImpuestosSearchPage(0); }} maxWidth="sm" fullWidth>
        <DialogTitle>Buscar impuesto</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={impuestoSearchTerm}
              onChange={(e) => setImpuestoSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setImpuestosSearchPage(0), loadImpuestosSearch())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Button variant="outlined" onClick={() => { setImpuestosSearchPage(0); loadImpuestosSearch(); }} sx={{ color: '#1976d2', borderColor: '#1976d2' }}>
              Buscar
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {impuestosSearchResults.map((imp) => {
                  const codigo = (imp.codigoImpuesto || imp.CodigoImpuesto || '').trim();
                  const desc = (imp.descripcion || imp.Descripcion || '-').trim();
                  const tipo = (imp.tipo || imp.Tipo || '-').trim();
                  return (
                    <TableRow key={codigo} hover>
                      <TableCell>{codigo}</TableCell>
                      <TableCell>{desc}</TableCell>
                      <TableCell>{tipo}</TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => handleSelectImpuesto(imp)}>
                          Seleccionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={impuestosSearchTotal}
            page={impuestosSearchPage}
            onPageChange={(_, p) => setImpuestosSearchPage(p)}
            rowsPerPage={10}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenImpuestoSearch(false); setImpuestoSearchTerm(''); setImpuestosSearchPage(0); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openProductSearch}
        onClose={() => {
          setOpenProductSearch(false);
          setProductSearchIndex(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            pb: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Buscar artículo
          </Typography>
          <IconButton
            aria-label="Cerrar"
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setProductosPage(0), loadProductos())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => {
                setProductosPage(0);
                loadProductos();
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2', flexShrink: 0 }}
            >
              Buscar
            </Button>
          </Stack>

          {isMobile ? (
            <Stack spacing={1.5}>
              {productos.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ py: 1 }}>
                  Sin resultados.
                </Typography>
              ) : (
                productos.map((p) => {
                  const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis);
                  const esDis = esTipoDiscontinuado(p.tipoArticulo);
                  return (
                  <Card key={p.codigoArticulo || p.codigo || ''} variant="outlined" sx={{ borderRadius: 2, opacity: disBloqueado ? 0.75 : 1 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
                          {p.codigoArticulo || p.codigo || '-'}
                        </Typography>
                        {esDis && <Chip size="small" label="DIS" color="warning" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                        {p.descripcion || p.descripcionArticulo || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Costo: {formatCurrency(Number(p.costo) || 0)}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={disBloqueado}
                        onClick={() => handleSelectProduct(p)}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        {disBloqueado ? 'No seleccionable' : 'Seleccionar'}
                      </Button>
                    </CardContent>
                  </Card>
                  );
                })
              )}
            </Stack>
          ) : (
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Costo</TableCell>
                    <TableCell align="center">Seleccionar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productos.map((p) => {
                    const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(p.tipoArticulo, modoBloqueoDis);
                    const esDis = esTipoDiscontinuado(p.tipoArticulo);
                    return (
                    <TableRow key={p.codigoArticulo || p.codigo || ''} hover sx={{ opacity: disBloqueado ? 0.75 : 1 }}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{p.codigoArticulo || p.codigo || '-'}</span>
                          {esDis && <Chip size="small" label="DIS" color="warning" />}
                        </Stack>
                      </TableCell>
                      <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(p.costo) || 0)}</TableCell>
                      <TableCell align="center">
                        <Button size="small" disabled={disBloqueado} onClick={() => handleSelectProduct(p)}>
                          {disBloqueado ? 'No seleccionable' : 'Seleccionar'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              mt: 1,
              '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            fullWidth={isMobile}
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPendientes} onClose={() => {
        if (agregandoTodos) return;
        setOpenPendientes(false);
      }} maxWidth="lg" fullWidth>
        <DialogTitle>Buscar pendientes</DialogTitle>
        <DialogContent>
          {loadingPendientes ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2, mt: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por comprobante, código o descripción..."
                  value={pendientesBusqueda}
                  onChange={(e) => setPendientesBusqueda(e.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Fecha emisión desde"
                  type="date"
                  value={pendientesFechaDesde}
                  onChange={(e) => setPendientesFechaDesde(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 170 } }}
                />
                <TextField
                  label="Fecha emisión hasta"
                  type="date"
                  value={pendientesFechaHasta}
                  onChange={(e) => setPendientesFechaHasta(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 170 } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={soloInventariados}
                      onChange={(e) => setSoloInventariados(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Solo inventariados"
                  sx={{ ml: 0, whiteSpace: 'nowrap' }}
                />
              </Stack>
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={pendientesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha emisión</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cant. pedida</TableCell>
                    <TableCell align="right">Pend. facturar</TableCell>
                    <TableCell align="right">Pend. recibir</TableCell>
                    <TableCell align="center" sx={pendientesAccionCellSx}>
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientesFiltrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                        {pendientes.length === 0
                          ? 'No hay líneas con pendiente de facturar para este proveedor.'
                          : pendientesDisponibles.length === 0
                            ? 'Las líneas pendientes ya fueron agregadas a esta factura.'
                            : 'No hay pendientes que coincidan con los filtros aplicados.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendientesFiltrados.map((p) => (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}</TableCell>
                      <TableCell sx={pendientesArticuloCellSx}>{p.codigoArticulo} - {p.descripcionArticulo}</TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell align="right">{p.pendienteFacturar}</TableCell>
                      <TableCell align="right">{p.pendienteRecibir}</TableCell>
                      <TableCell align="center" sx={pendientesAccionCellSx}>
                        <Tooltip title="Ver documentos facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" onClick={() => void agregarPendienteProducto(p)}>
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void agregarTodosPendientes()}
            disabled={pendientesFiltrados.length === 0 || loadingPendientes || agregandoTodos}
            startIcon={agregandoTodos ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {agregandoTodos ? 'Agregando…' : 'Agregar todos'}
          </Button>
          <Button onClick={() => setOpenPendientes(false)} disabled={agregandoTodos}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />

      <Dialog
        open={confirmAplicarOpen}
        onClose={handleIrAlDetalleSinAplicar}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Aplicar saldo pendiente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            El proveedor tiene órdenes de pago o notas de crédito con saldo pendiente. ¿Desea aplicarlas ahora?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIrAlDetalleSinAplicar} sx={{ textTransform: 'none' }}>
            No
          </Button>
          <Button
            variant="contained"
            onClick={handleIrAlDetalleYAplicar}
            sx={{ textTransform: 'none' }}
          >
            Sí
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacturaCompraForm;